/**
 * Skylite Desktop OS Interface
 * Copyright © 2024 Enterceptor Corp
 * All Rights Reserved
 * 
 * This file and its contents are protected by copyright law.
 * Unauthorized reproduction or distribution is prohibited.
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TerminalIcon from '@mui/icons-material/Terminal';
import ScrollIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import BugReportIcon from '@mui/icons-material/BugReport';
import MinimizeIcon from '@mui/icons-material/MinimizeRounded';
import MaximizeIcon from '@mui/icons-material/CropSquare';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LogoutIcon from '@mui/icons-material/Logout';
import SchoolIcon from '@mui/icons-material/School';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableSortLabel from '@mui/material/TableSortLabel';
import Chip from '@mui/material/Chip';
import Terminal from './components/Terminal.js';
import ReportSummary from './components/ReportSummary.js';
import Files from './components/Files.js';
import Browser from './components/Browser.js';
import LanguageSelector from './components/LanguageSelector.js';
import LandingPortal from './components/LandingPortal.js';
import LicenseGateOverlay from './components/LicenseGateOverlay.js';
import FirstWelcomeOverlay, {
  markFirstWelcomePending,
  markFirstWelcomeSeen,
  shouldShowFirstWelcome
} from './components/FirstWelcomeOverlay.js';
import OwnerLicenseConsole from './components/OwnerLicenseConsole.js';
import OwnerAdminDashboard from './components/OwnerAdminDashboard.js';
import SupportTicketPanel from './components/SupportTicketPanel.js';
import InstitutionLeaderDashboard from './components/InstitutionLeaderDashboard.js';
import { useLanguage } from './contexts/LanguageContext.js';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import './styles.css';
import './App.css';
import { getLearningDashboardUrl } from './lib/learningDashboardUrl.js';
import { appLanguageToGoogleCode, getTranslation } from './config/languages.js';
import {
  DEMO_PRIVACY_BLUR_CLASS,
  DEMO_PRIVACY_CSS,
  applyDemoPrivacyBodyClass,
  isDemoPrivacyBlurOn,
  setDemoPrivacyBlur,
  toggleDemoPrivacyBlur
} from './lib/demoPrivacyBlur.js';
import {
  INSTITUTION_HUB_PATH,
  OWNER_ADMIN_HUB_PATH,
  readImpersonationEnglishChrome,
  setImpersonationEnglishChrome,
  clearImpersonationEnglishChrome,
  readInstitutionDesktopEntered,
  setInstitutionDesktopEntered as persistInstitutionDesktopEntered,
  clearInstitutionDesktopEntered,
  readSupportPreviewUser,
  writeSupportPreviewUser,
  readOwnerInstitutionPreviewActive,
  setOwnerInstitutionPreviewActive as persistOwnerInstitutionPreviewActive,
  clearAllOwnerPreviewSession
} from './lib/institutionLeaderHubSession.js';

/** Left drawer width when expanded; narrow rail when collapsed */
const LEFT_PANEL_EXPANDED_W = 236;
const LEFT_PANEL_RAIL_W = 36;
const BOX_START_PREP_DELAY_MS = 20000;

/** When set, owner skipped hub and is on Skylite desktop (survives refresh in this tab). */
const OWNER_HUB_SESSION_KEY = 'skylite_owner_desktop_mode';

/** Training "Dashboard" / `?ownerHub=1` — survives Strict Mode and path sync (must match learning-session-manager.js). */
const OWNER_HUB_NAV_INTENT_KEY = 'skylite_owner_hub_nav_intent';

/** Client-only UI shell override for teacher/admin/tester/owner (see taskbar "View shell"). */
const SHELL_PREVIEW_STORAGE_KEY = 'skylite_shell_preview_role';


/** Comma-separated lowercased emails that skip the license gate if `/api/session` omits `isOwnerConsole` (local dev). Set `REACT_APP_OWNER_LICENSE_GATE_BYPASS` in `.env`; production defaults to empty (no client bypass). */
function parseCommaLowerEmailSet(raw) {
  if (raw == null || typeof raw !== 'string') return new Set();
  return new Set(raw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean));
}
const _ownerBypassRaw =
  typeof process !== 'undefined' && process.env && process.env.REACT_APP_OWNER_LICENSE_GATE_BYPASS;
const OWNER_LICENSE_GATE_BYPASS_EMAILS =
  _ownerBypassRaw != null && String(_ownerBypassRaw).trim() !== ''
    ? parseCommaLowerEmailSet(String(_ownerBypassRaw))
    : process.env.NODE_ENV === 'production'
      ? new Set()
      : new Set(['james.houston@enterceptor.com']);

/** Non-production only: set from License gate "Skip…" button; survives hard refresh until cleared. */
const SKYLITE_DEV_LICENSE_SKIP_KEY = 'skylite_dev_skip_license_gate_v1';
const SKYLITE_DEMO_ACCESS_KEY = 'skylite_demo_access_v1';
const SKYLITE_DEMO_DESKTOP_QUERY_KEY = 'skyliteDesktop';
const SPHINX_DEMO_PROGRESS_STORAGE_KEY = 'sphinx_demo_progress_v1';

function readSkyliteDevLicenseSkip() {
  if (typeof process === 'undefined' || process.env.NODE_ENV === 'production') return false;
  try {
    return localStorage.getItem(SKYLITE_DEV_LICENSE_SKIP_KEY) === '1';
  } catch {
    return false;
  }
}

/** Demo product path retired — clear leftover flags only. */
function clearRetiredDemoStorage() {
  try {
    localStorage.removeItem(SKYLITE_DEMO_ACCESS_KEY);
    sessionStorage.removeItem(SPHINX_DEMO_PROGRESS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function clearGoogTransCookiesAtCurrentHost() {
  try {
    const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const hostname = window.location.hostname || '';
    const parts = hostname.split('.').filter(Boolean);
    const parentDomain = parts.length > 2 ? parts.slice(-2).join('.') : '';
    const domains = [hostname, hostname ? `.${hostname}` : '', parentDomain, parentDomain ? `.${parentDomain}` : ''].filter(Boolean);
    const path = window.location.pathname || '/';
    const paths = ['/', path, path.replace(/\/[^/]*$/, '/') || '/'];
    domains.forEach((domain) => {
      paths.forEach((cookiePath) => {
        document.cookie = `googtrans=;expires=${past};max-age=0;domain=${domain};path=${cookiePath}`;
      });
    });
    paths.forEach((cookiePath) => {
      document.cookie = `googtrans=;expires=${past};max-age=0;path=${cookiePath}`;
    });
  } catch (_) {
    /* ignore */
  }
}

function clearSkyliteDemoBrowserState() {
  try {
    localStorage.removeItem(SKYLITE_DEMO_ACCESS_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(SPHINX_DEMO_PROGRESS_STORAGE_KEY);
    sessionStorage.removeItem(OWNER_HUB_SESSION_KEY);
    sessionStorage.removeItem(OWNER_HUB_NAV_INTENT_KEY);
    sessionStorage.removeItem(SHELL_PREVIEW_STORAGE_KEY);
    sessionStorage.removeItem('skylite_institution_desktop');
  } catch {
    /* ignore */
  }
}

const DEFAULT_WINDOW_VIEWPORT_OFFSET = 112;
const BROWSER_WINDOW_VIEWPORT_OFFSET = 30;

const ROLE_SHELLS = {
  student: {
    path: '/student',
    allowedWindows: ['terminal', 'browser'],
    menuGroupIds: ['workspace']
  },
  teacher: {
    path: '/teacher',
    allowedWindows: ['terminal', 'report', 'files', 'browser'],
    menuGroupIds: ['recon', 'reporting', 'workspace']
  },
  admin: {
    path: '/admin',
    allowedWindows: ['terminal', 'report', 'files', 'browser'],
    menuGroupIds: ['recon', 'reporting', 'workspace']
  },
  tester: {
    path: '/admin',
    allowedWindows: ['terminal', 'report', 'files', 'browser'],
    menuGroupIds: ['recon', 'reporting', 'workspace']
  }
};

/** Base-model lab boxes; Box 01 launches the Scout container target */
const LAB_BOX_PLACEHOLDERS = [
  { id: 'box-01', labelKey: 'box01Lab', targetUrl: 'http://localhost:8081' },
  { id: 'box-02', labelKey: 'box02Lab' },
  { id: 'box-03', labelKey: 'box03Lab' },
  { id: 'box-04', labelKey: 'box04Lab' }
];

/** OAuth return path (relative). Server resolves to `APP_PUBLIC_ORIGIN` so users land on Sphinx first, not the training site. */
const POST_LOGIN_SPA_PATH = '/';

const landingTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#22d3ee' },
    secondary: { main: '#0d9488' },
    background: { default: '#040810', paper: '#121c2f' },
    text: { primary: '#e8f4ff', secondary: '#94a3b8' },
    divider: 'rgba(120, 200, 255, 0.2)'
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          color: '#031018',
          background: 'linear-gradient(90deg, #22d3ee 0%, #67e8f9 100%)',
          boxShadow: '0 0 18px rgba(34, 211, 238, 0.24)',
          '&:hover': {
            background: 'linear-gradient(90deg, #06b6d4 0%, #22d3ee 100%)',
            boxShadow: '0 0 24px rgba(34, 211, 238, 0.34)',
          },
        },
      },
    },
  }
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1a1a1a',
      paper: '#2d2d2d',
    },
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const DesktopIcon = ({ icon: Icon, label, onClick }) => {
  const { t } = useLanguage();
  
  return (
  <Box
    className="desktop-icon"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      cursor: 'pointer',
      width: 80,
      m: 1,
      p: 1,
      borderRadius: 2,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        transform: 'scale(1.05)',
      },
    }}
    onClick={onClick}
  >
    <Icon sx={{ 
      fontSize: 40, 
      color: '#d7f3ff', 
      mb: 1,
      filter: 'drop-shadow(0 0 8px rgba(113, 224, 255, 0.55))'
    }} />
    <Typography
      variant="caption"
      sx={{
        color: 'white',
        textAlign: 'center',
        textShadow: '0 0 8px rgba(122, 230, 255, 0.55)',
        fontSize: '0.76rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
      }}
    >
        {t(label)}
    </Typography>
  </Box>
);
};

/** Lab VM tile — same visual weight as desktop icons (icon ≤40px); onClick wired later */
const LabBoxTile = ({ labelKey, onClick, disabled = false, statusText = '' }) => {
  const { t } = useLanguage();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e);
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        p: 0.75,
        borderRadius: 1.25,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.75 : 1,
        width: '100%',
        maxWidth: 92,
        mx: 'auto',
        border: '1px solid rgba(140, 214, 255, 0.22)',
        backgroundColor: 'rgba(12, 22, 44, 0.5)',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: 'rgba(42, 86, 120, 0.42)',
          borderColor: 'rgba(180, 235, 255, 0.45)',
          transform: 'scale(1.03)'
        }
      }}
    >
      <DnsOutlinedIcon
        sx={{
          fontSize: 36,
          color: '#a8e6ff',
          mb: 0.5,
          filter: 'drop-shadow(0 0 6px rgba(113, 224, 255, 0.4))'
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(230, 248, 255, 0.92)',
          textAlign: 'center',
          fontSize: '0.68rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          lineHeight: 1.2
        }}
      >
        {t(labelKey)}
      </Typography>
      {statusText ? (
        <Typography
          variant="caption"
          sx={{
            color: '#f7d88b',
            textAlign: 'center',
            fontSize: '0.58rem',
            fontWeight: 600,
            letterSpacing: '0.03em',
            lineHeight: 1.1
          }}
        >
          {statusText}
        </Typography>
      ) : null}
    </Box>
  );
};

function App() {
  const BROWSER_TARGET_STORAGE_KEY = 'skylite_target_ip';
  const BOX01_BOOT_ENDPOINT = '/api/lab-boxes/box-01/start';
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authProviders, setAuthProviders] = useState(null);
  const [, setDevAuthShortcuts] = useState(false);
  const [isOwnerConsole, setIsOwnerConsole] = useState(false);
  const [ownerHubVisible, setOwnerHubVisible] = useState(false);
  const [supportTicketOpen, setSupportTicketOpen] = useState(false);
  const [supportTicketMode, setSupportTicketMode] = useState('submit');
  const [licenseGatePreviewMode, setLicenseGatePreviewMode] = useState(null);
  const [devLicenseSkipAck, setDevLicenseSkipAck] = useState(() => readSkyliteDevLicenseSkip());
  const [personalLicense, setPersonalLicense] = useState(null);
  /** One-time CEO welcome after first license redeem (localStorage pending/seen). */
  const [showFirstWelcome, setShowFirstWelcome] = useState(false);
  /** Keep loading chrome while navigating away — never flash Skylite OS. */
  const [holdForLearningRedirect, setHoldForLearningRedirect] = useState(false);
  const [windows, setWindows] = useState([]);
  const [activeWindow, setActiveWindow] = useState(null);
  const [zIndexCounter, setZIndexCounter] = useState(1000);
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const [windowSizes, setWindowSizes] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenuGroup, setActiveMenuGroup] = useState('recon');
  const [leftPanelExpanded, setLeftPanelExpanded] = useState(true);
  const [boxLaunchState, setBoxLaunchState] = useState({});
  const [environmentPreparing, setEnvironmentPreparing] = useState(false);
  const [switchingInstitutionId, setSwitchingInstitutionId] = useState('');
  const [isSwitchingInstitution, setIsSwitchingInstitution] = useState(false);
  const [institutionSwitchError, setInstitutionSwitchError] = useState('');
  const [identityMode, setIdentityMode] = useState('userId');
  const [memberTargetUserId, setMemberTargetUserId] = useState('');
  const [memberTargetEmail, setMemberTargetEmail] = useState('');
  const [memberTargetLearnerProfileId, setMemberTargetLearnerProfileId] = useState('');
  const [memberIdentifierType, setMemberIdentifierType] = useState('');
  const [memberIdentifierValue, setMemberIdentifierValue] = useState('');
  const [memberTargetStatus, setMemberTargetStatus] = useState('suspended');
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  const [memberActionMessage, setMemberActionMessage] = useState('');
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberStatusFilter, setMemberStatusFilter] = useState('all');
  const [memberSortBy, setMemberSortBy] = useState('user');
  const [memberSortDir, setMemberSortDir] = useState('asc');
  const [seatSummary, setSeatSummary] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize] = useState(8);
  const [auditHasNextPage, setAuditHasNextPage] = useState(false);
  const [auditEventTypeFilter, setAuditEventTypeFilter] = useState('all');
  const [auditSearch, setAuditSearch] = useState('');
  const [shellPreviewRole, setShellPreviewRole] = useState(() => {
    try {
      const v = sessionStorage.getItem(SHELL_PREVIEW_STORAGE_KEY);
      const lc = String(v || '').toLowerCase();
      return ROLE_SHELLS[lc] ? lc : '';
    } catch {
      return '';
    }
  });
  const [shellPreviewMenuAnchor, setShellPreviewMenuAnchor] = useState(null);
  const [institutionDesktopEntered, setInstitutionDesktopEntered] = useState(() => readInstitutionDesktopEntered());
  const [supportPreviewUser, setSupportPreviewUser] = useState(() => readSupportPreviewUser());
  const [ownerInstitutionPreviewActive, setOwnerInstitutionPreviewActive] = useState(() =>
    readOwnerInstitutionPreviewActive()
  );
  const [impersonation, setImpersonation] = useState(null);
  const [demoPrivacyBlur, setDemoPrivacyBlurState] = useState(() => isDemoPrivacyBlurOn());
  /** Owner license console: full-screen page (not a transparent desktop window). */
  const [licenseConsolePageOpen, setLicenseConsolePageOpen] = useState(false);
  const terminalRef = useRef(null);
  const { t, setForceEnglishDisplay } = useLanguage();
  const activeInstitutionMembershipStatus = String(user?.activeInstitutionMembershipStatus || '').toLowerCase();
  const institutionAccessBlocked = activeInstitutionMembershipStatus === 'suspended' || activeInstitutionMembershipStatus === 'invited';
  const activeMemberships = useMemo(
    () => (Array.isArray(user?.activeMemberships) ? user.activeMemberships : []),
    [user?.activeMemberships]
  );
  const institutionAccessMessage = activeInstitutionMembershipStatus === 'suspended'
    ? 'Your active institution membership is suspended. Switch to another institution or contact your administrator.'
    : activeInstitutionMembershipStatus === 'invited'
      ? 'Your active institution membership is not active yet. Accept or activate your invite, or switch institutions.'
      : '';
  const sessionRole = String(user?.role || 'student').toLowerCase();
  const canManageMemberships = ['admin', 'teacher', 'tester'].includes(sessionRole);
  const ownerEmailLc = String(user?.email || '').trim().toLowerCase();
  const ownerLicenseGateBypass =
    Boolean(ownerEmailLc) && OWNER_LICENSE_GATE_BYPASS_EMAILS.has(ownerEmailLc);
  const devLicenseGateSkip = devLicenseSkipAck || readSkyliteDevLicenseSkip();
  const effectiveIsOwnerConsole = isOwnerConsole || ownerLicenseGateBypass || devLicenseGateSkip;
  // Demo product path retired
  const demoDisplayActive = false;
  const canManageMembershipsUi = canManageMemberships && !demoDisplayActive;
  const canShellPreview =
    Boolean(user?.id) &&
    !demoDisplayActive &&
    (effectiveIsOwnerConsole || ['admin', 'tester', 'teacher'].includes(sessionRole));
  const shellPreviewActive =
    canShellPreview && shellPreviewRole && ROLE_SHELLS[shellPreviewRole] ? shellPreviewRole : null;
  const effectiveRole = demoDisplayActive ? 'student' : (shellPreviewActive || sessionRole);
  const shellConfig = ROLE_SHELLS[effectiveRole] || ROLE_SHELLS.student;
  const resetOwnerDesktopLanguage = useCallback(() => {
    try {
      clearGoogTransCookiesAtCurrentHost();
      localStorage.removeItem('googtrans');
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
      document.body?.setAttribute('dir', 'ltr');
      document.body?.setAttribute('lang', 'en');
      // Suspend GT display only — do NOT wipe commandline_selected_language (that caused Arabic to
      // "pop back" when Learning Center read the leftover twin key after owner chrome forced en).
      const mgr = window.__skyliteAutoTranslateManager;
      if (mgr && typeof mgr.suspendTranslation === 'function') {
        mgr.suspendTranslation();
      } else if (mgr && typeof mgr.applyLanguage === 'function') {
        mgr.applyLanguage('en', { persist: false, reloadIfUnavailable: false, force: true });
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const resumeUserLanguageTranslation = useCallback(() => {
    try {
      const mgr = window.__skyliteAutoTranslateManager;
      if (mgr && typeof mgr.resumeTranslation === 'function') {
        mgr.resumeTranslation();
        return;
      }
      if (mgr && typeof mgr.applyLanguage === 'function') {
        const g =
          localStorage.getItem('commandline_selected_language') ||
          localStorage.getItem('skylite-google-translate') ||
          appLanguageToGoogleCode(localStorage.getItem('skylite-language') || 'en');
        mgr.applyLanguage(g, {
          persist: false,
          reloadIfUnavailable: false,
          force: true
        });
      }
    } catch (_) {
      /* ignore */
    }
  }, []);
  const allowedWindowTypes = useMemo(() => {
    const base = shellConfig.allowedWindows;
    return base;
  }, [shellConfig.allowedWindows]);
  const applyShellPreview = useCallback((nextKey) => {
    const lc = String(nextKey || '').toLowerCase();
    const normalized = lc && ROLE_SHELLS[lc] ? lc : '';
    setShellPreviewRole(normalized);
    setShellPreviewMenuAnchor(null);
    try {
      if (normalized) sessionStorage.setItem(SHELL_PREVIEW_STORAGE_KEY, normalized);
      else sessionStorage.removeItem(SHELL_PREVIEW_STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const ownerLicensePreviewLicense =
    licenseGatePreviewMode === 'expired'
      ? { status: 'expired', expiresAt: new Date(Date.now() - 86400000).toISOString() }
      : licenseGatePreviewMode === 'needs_code'
        ? { status: 'needs_code', expiresAt: null }
        : null;
  const demoAccessActive = false;
  const accountStripDisplayLabel =
    user?.email || user?.name || user?.displayName || t('ownerDefaultDisplayName');
  const accountStripDisplayTitle =
    [user?.email, user?.name || user?.displayName].filter(Boolean).join(' · ') || '';

  const showLicenseGate =
    (Boolean(user) && !effectiveIsOwnerConsole && !demoAccessActive && personalLicense && personalLicense.status !== 'active') ||
    (Boolean(user) && effectiveIsOwnerConsole && !demoDisplayActive && ownerLicensePreviewLicense);

  const licenseGateOverlayProps =
    effectiveIsOwnerConsole && ownerLicensePreviewLicense
      ? { personalLicense: ownerLicensePreviewLicense, onClosePreview: () => setLicenseGatePreviewMode(null) }
      : { personalLicense, onClosePreview: undefined };

  const showInstitutionLeaderHub =
    Boolean(user?.id) &&
    !showLicenseGate &&
    !institutionAccessBlocked &&
    ((canManageMembershipsUi && !effectiveIsOwnerConsole && !institutionDesktopEntered) ||
      (!demoDisplayActive && effectiveIsOwnerConsole && ownerInstitutionPreviewActive && Boolean(supportPreviewUser)));
  const ownerDesktopActive =
    Boolean(user?.id) &&
    effectiveIsOwnerConsole &&
    !demoDisplayActive &&
    !ownerHubVisible &&
    !showInstitutionLeaderHub &&
    !licenseConsolePageOpen &&
    !showLicenseGate;

  const activeMembershipRow =
    Array.isArray(user?.activeMemberships) && user?.institutionId != null
      ? user.activeMemberships.find((m) => m.institutionId === user.institutionId)
      : null;
  const activeInstitutionName = activeMembershipRow?.institutionName || '';

  const getIdentitySelectorPayload = () => {
    if (identityMode === 'email') {
      return { email: memberTargetEmail.trim() };
    }
    if (identityMode === 'learnerProfileId') {
      return { learnerProfileId: memberTargetLearnerProfileId.trim() };
    }
    if (identityMode === 'identifier') {
      return {
        identifierType: memberIdentifierType.trim(),
        identifierValue: memberIdentifierValue.trim()
      };
    }
    return { userId: memberTargetUserId.trim() };
  };
  const getIdentitySelectorLabel = () => {
    if (identityMode === 'email') return memberTargetEmail.trim();
    if (identityMode === 'learnerProfileId') return `learnerProfileId=${memberTargetLearnerProfileId.trim()}`;
    if (identityMode === 'identifier') return `${memberIdentifierType.trim()}:${memberIdentifierValue.trim()}`;
    return `User ${memberTargetUserId.trim()}`;
  };
  const normalizedMemberSearch = memberSearch.trim().toLowerCase();
  const remainingSeats = seatSummary?.seatsRemaining;
  const seatHealthColor = (() => {
    if (!seatSummary) return 'rgba(210, 241, 255, 0.9)';
    if (seatSummary?.overLimit) return '#ff9ea3';
    if (remainingSeats == null) return 'rgba(210, 241, 255, 0.9)';
    if (remainingSeats <= 0) return '#ffb1b5';
    if (remainingSeats <= 2) return '#ffd28f';
    return '#b7f3c0';
  })();
  const seatHealthText = (() => {
    if (!seatSummary) return 'Seats: unavailable';
    const activeStudents = seatSummary?.counts?.activeStudents ?? 0;
    const seatLimit = seatSummary?.seatLimit ?? 'unlimited';
    const remaining = remainingSeats ?? 'unlimited';
    if (seatSummary?.overLimit) {
      return `Seats: ${activeStudents}/${seatLimit} active students (OVER LIMIT by ${Math.max(activeStudents - (seatSummary?.seatLimit ?? activeStudents), 0)})`;
    }
    return `Seats: ${activeStudents}/${seatLimit} active students (remaining: ${remaining})`;
  })();
  const visibleMembers = members.filter((m) => {
    const status = String(m.Status || '').toLowerCase();
    if (memberStatusFilter !== 'all' && status !== memberStatusFilter) return false;
    if (!normalizedMemberSearch) return true;
    const haystack = [
      m.UserName,
      m.UserEmail,
      String(m.UserId || ''),
      String(m.RoleAtInstitution || ''),
      String(m.Status || '')
    ].join(' ').toLowerCase();
    return haystack.includes(normalizedMemberSearch);
  }).sort((a, b) => {
    const factor = memberSortDir === 'asc' ? 1 : -1;
    const getSortable = (row) => {
      if (memberSortBy === 'role') return String(row.RoleAtInstitution || '').toLowerCase();
      if (memberSortBy === 'status') return String(row.Status || '').toLowerCase();
      return String(row.UserName || row.UserEmail || row.UserId || '').toLowerCase();
    };
    const av = getSortable(a);
    const bv = getSortable(b);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });

  const handleSortMembers = (field) => {
    if (memberSortBy === field) {
      setMemberSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setMemberSortBy(field);
    setMemberSortDir('asc');
  };

  useEffect(() => {
    if (!user) return;
    const currentPath = window.location.pathname || '/';

    if (showInstitutionLeaderHub) {
      if (currentPath !== INSTITUTION_HUB_PATH) {
        try {
          window.history.pushState({ institutionPreview: true }, '', INSTITUTION_HUB_PATH);
        } catch {
          window.history.pushState({ institutionPreview: true }, '', INSTITUTION_HUB_PATH);
        }
      }
      return;
    }

    if (effectiveIsOwnerConsole && !demoDisplayActive && ownerHubVisible && !licenseConsolePageOpen) {
      if (currentPath !== OWNER_ADMIN_HUB_PATH) {
        try {
          window.history.replaceState({ ownerHub: true }, '', OWNER_ADMIN_HUB_PATH);
        } catch {
          window.history.replaceState({ ownerHub: true }, '', OWNER_ADMIN_HUB_PATH);
        }
      }
      return;
    }

    if (!effectiveIsOwnerConsole) {
      const targetPath = shellConfig.path;
      if (
        currentPath !== targetPath &&
        currentPath !== INSTITUTION_HUB_PATH &&
        currentPath !== OWNER_ADMIN_HUB_PATH
      ) {
        try {
          const u = new URL(window.location.href);
          u.pathname = targetPath;
          window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
        } catch {
          window.history.replaceState({}, '', targetPath);
        }
      }
    }
  }, [
    user,
    shellConfig.path,
    showInstitutionLeaderHub,
    effectiveIsOwnerConsole,
    demoDisplayActive,
    ownerHubVisible,
    licenseConsolePageOpen
  ]);

  /** Seed history on reload so browser Back from preview returns to /owner (not a 404). */
  useEffect(() => {
    if (!user || !effectiveIsOwnerConsole || !showInstitutionLeaderHub) return;
    if (window.history.state?.institutionPreview) return;
    try {
      window.history.replaceState({ ownerHub: true }, '', OWNER_ADMIN_HUB_PATH);
      window.history.pushState({ institutionPreview: true }, '', INSTITUTION_HUB_PATH);
    } catch {
      /* ignore */
    }
  }, [user, effectiveIsOwnerConsole, showInstitutionLeaderHub]);

  /**
   * Keep owner/teacher/admin/license chrome English.
   * forceEnglishDisplay is also seeded at boot for /owner and /institution-hub so the first paint is English.
   * Impersonation stays English too — otherwise the owner's stored language (often Arabic from testing)
   * resumes on the teacher desktop and class-list retranslate paints RTL over the dashboard.
   * Critical: while session auth is still loading, do NOT resume Arabic — impersonation is unknown
   * until /api/session returns, and that race was the start→reject Translate glitch.
   * Do NOT resume Arabic in effect cleanup (that raced and re-applied /en/ar on every re-render).
   */
  useLayoutEffect(() => {
    const sessionImpersonationChrome = readImpersonationEnglishChrome();
    const forceEnglishChrome =
      ownerHubVisible ||
      ownerDesktopActive ||
      licenseConsolePageOpen ||
      showLicenseGate ||
      showInstitutionLeaderHub ||
      Boolean(impersonation?.active) ||
      sessionImpersonationChrome;

    // Auth not settled yet: keep boot English freeze; never resume stored language.
    if (isLoading && !forceEnglishChrome) {
      try {
        window.__skyliteForceEnglishChrome = true;
      } catch (_) {
        /* ignore */
      }
      if (typeof setForceEnglishDisplay === 'function') setForceEnglishDisplay(true);
      resetOwnerDesktopLanguage();
      return undefined;
    }

    if (!forceEnglishChrome) {
      try {
        window.__skyliteForceEnglishChrome = false;
      } catch (_) {
        /* ignore */
      }
      if (typeof setForceEnglishDisplay === 'function') setForceEnglishDisplay(false);
      resumeUserLanguageTranslation();
      return undefined;
    }

    try {
      window.__skyliteForceEnglishChrome = true;
    } catch (_) {
      /* ignore */
    }
    if (typeof setForceEnglishDisplay === 'function') setForceEnglishDisplay(true);
    resetOwnerDesktopLanguage();
    const timer = window.setTimeout(resetOwnerDesktopLanguage, 250);
    const timer2 = window.setTimeout(resetOwnerDesktopLanguage, 1200);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(timer2);
    };
  }, [
    isLoading,
    ownerHubVisible,
    ownerDesktopActive,
    licenseConsolePageOpen,
    showLicenseGate,
    showInstitutionLeaderHub,
    impersonation?.active,
    resetOwnerDesktopLanguage,
    resumeUserLanguageTranslation,
    setForceEnglishDisplay
  ]);

  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname || '/';
      if (path === INSTITUTION_HUB_PATH) return;
      if (readOwnerInstitutionPreviewActive() || ownerInstitutionPreviewActive) {
        clearAllOwnerPreviewSession();
        setSupportPreviewUser(null);
        setOwnerInstitutionPreviewActive(false);
        setOwnerHubVisible(true);
        if (path !== OWNER_ADMIN_HUB_PATH) {
          try {
            window.history.replaceState({ ownerHub: true }, '', OWNER_ADMIN_HUB_PATH);
          } catch {
            /* ignore */
          }
        }
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [ownerInstitutionPreviewActive]);

  useEffect(() => {
    if (!user?.id) return;
    if (!canShellPreview && shellPreviewRole) {
      applyShellPreview('');
    }
  }, [user?.id, canShellPreview, shellPreviewRole, applyShellPreview]);

  useEffect(() => {
    if (!user?.id) setLicenseConsolePageOpen(false);
  }, [user?.id]);

  // Safety net: licensed students stay on Learning only — never Skylite OS / hubs.
  useEffect(() => {
    if (isLoading || holdForLearningRedirect || !user?.id || showLicenseGate || showFirstWelcome) return;
    if (effectiveIsOwnerConsole || demoDisplayActive) return;
    if (String(user.role || '').toLowerCase() !== 'student') return;
    if (personalLicense?.status !== 'active') return;
    setHoldForLearningRedirect(true);
    window.location.replace(getLearningDashboardUrl({ openDashboard: true }));
  }, [
    isLoading,
    holdForLearningRedirect,
    user?.id,
    user?.role,
    personalLicense?.status,
    showLicenseGate,
    showFirstWelcome,
    effectiveIsOwnerConsole,
    demoDisplayActive
  ]);

  useEffect(() => {
    setWindows((prev) => {
      const next = prev.filter((w) => allowedWindowTypes.includes(w.type));
      if (
        next.length === prev.length &&
        next.every((w, i) => w.id === prev[i].id && w.type === prev[i].type)
      ) {
        return prev;
      }
      return next;
    });
  }, [allowedWindowTypes]);

  useEffect(() => {
    setMinimizedWindows((mw) => {
      const next = mw.filter((id) => windows.some((w) => w.id === id));
      if (next.length === mw.length && next.every((id, i) => id === mw[i])) return mw;
      return next;
    });
    setActiveWindow((aw) => {
      if (aw == null) return aw;
      return windows.some((w) => w.id === aw) ? aw : null;
    });
  }, [windows]);

  const summarizeAuditEvent = (item) => {
    const eventType = String(item?.eventType || '');
    const payload = item?.payload;
    if (eventType === 'institution.member.status.updated' && payload && typeof payload === 'object') {
      const target = payload.targetUserId != null ? `User ${payload.targetUserId}` : 'member';
      return `${target}: ${payload.previousStatus || '?'} -> ${payload.newStatus || '?'}`;
    }
    if (eventType === 'institution.member.dropped' && payload && typeof payload === 'object') {
      const target = payload.targetUserId != null ? `User ${payload.targetUserId}` : 'member';
      return `${target} dropped (${payload.resultingStatus || 'suspended'})`;
    }
    if (eventType === 'institution.member.added_or_updated' && payload && typeof payload === 'object') {
      const target = payload.targetUserId != null ? `User ${payload.targetUserId}` : 'member';
      return `${target} ${payload.status || 'updated'} as ${payload.roleAtInstitution || 'member'}`;
    }
    if (eventType.startsWith('session.active_institution.denied') && payload && typeof payload === 'object') {
      return `Denied switch to institution ${payload.requestedInstitutionId || '?'}`;
    }
    if (eventType === 'session.active_institution.switched' && payload && typeof payload === 'object') {
      return `Switched to institution ${payload.requestedInstitutionId || '?'}`;
    }
    let text = '';
    if (payload == null) {
      text = '-';
    } else if (typeof payload === 'string') {
      text = payload;
    } else {
      try {
        text = JSON.stringify(payload);
      } catch (_) {
        text = '[payload]';
      }
    }
    return text.length > 120 ? `${text.slice(0, 117)}...` : text;
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const getWindowMaxHeight = (windowType) =>
    Math.max(
      260,
      window.innerHeight - (windowType === 'browser' ? BROWSER_WINDOW_VIEWPORT_OFFSET : DEFAULT_WINDOW_VIEWPORT_OFFSET)
    );

  const leftPanelOffset = leftPanelExpanded ? LEFT_PANEL_EXPANDED_W : LEFT_PANEL_RAIL_W;

  const activateBrowserDemoSession = useCallback((options = {}) => {
    // Demo retired — never arm demo session; clear leftovers instead
    void options;
    clearSkyliteDemoBrowserState();
    clearRetiredDemoStorage();
  }, []);

  const collapseLeftPanel = () => {
    setLeftPanelExpanded(false);
    setMenuOpen(false);
  };

  useEffect(() => {
    const checkUserSession = async () => {
      try {
        try {
          const qs = new URLSearchParams(window.location.search);
          if (qs.get('exitDemo') === '1') {
            clearSkyliteDemoBrowserState();
            clearRetiredDemoStorage();
            qs.delete('exitDemo');
            qs.delete('demo');
            qs.delete('demoTraining');
            const query = qs.toString();
            const path = window.location.pathname || '/';
            window.history.replaceState({}, '', query ? `${path}?${query}` : path);
          }
          // Demo product path retired — strip demo params; send old Try Demo links to real training
          const demoParam = qs.get('demo');
          const demoTrainingParam = qs.get('demoTraining');
          const hadDemoLink =
            demoParam === '1' || demoParam === 'training' || demoTrainingParam === '1';
          if (hadDemoLink) {
            clearSkyliteDemoBrowserState();
            clearRetiredDemoStorage();
            qs.delete('demo');
            qs.delete('demoTraining');
            qs.delete(SKYLITE_DEMO_DESKTOP_QUERY_KEY);
            const query = qs.toString();
            const path = window.location.pathname || '/';
            window.history.replaceState({}, '', query ? `${path}?${query}` : path);
            setHoldForLearningRedirect(true);
            window.location.replace(getLearningDashboardUrl({ openDashboard: true }));
            return;
          }
          if (qs.get('ownerHub') === '1') {
            sessionStorage.setItem(OWNER_HUB_NAV_INTENT_KEY, '1');
          }
        } catch {
          /* ignore */
        }
        let stayOnLoadingForLearning = false;
        const response = await fetch('/api/session', {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const userData = await response.json();
          if (userData.loggedIn) {
            const bypassHub =
              Boolean(userData.user?.email) &&
              OWNER_LICENSE_GATE_BYPASS_EMAILS.has(String(userData.user.email).trim().toLowerCase());
            const ownerPath =
              Boolean(userData.isOwnerConsole) || bypassHub || readSkyliteDevLicenseSkip();
            const roleLc = String(userData.user?.role || 'student').toLowerCase();
            const licenseOk = userData.personalLicense?.status === 'active';
            const welcomePending = shouldShowFirstWelcome(userData.user?.id);
            // Students: Learning only — never paint Skylite OS / institution hubs.
            if (
              !ownerPath &&
              roleLc === 'student' &&
              licenseOk &&
              !welcomePending
            ) {
              stayOnLoadingForLearning = true;
              setHoldForLearningRedirect(true);
              window.location.replace(getLearningDashboardUrl({ openDashboard: true }));
              return;
            }

            setUser(userData.user);
            setDevAuthShortcuts(Boolean(userData.devAuthShortcuts));
            setIsOwnerConsole(Boolean(userData.isOwnerConsole));
            setPersonalLicense(userData.personalLicense ?? null);
            setImpersonation(
              userData.impersonation?.active
                ? userData.impersonation
                : null
            );
            if (userData.impersonation?.active) {
              setImpersonationEnglishChrome();
              clearAllOwnerPreviewSession();
              setSupportPreviewUser(null);
              setOwnerInstitutionPreviewActive(false);
            } else {
              clearImpersonationEnglishChrome();
            }
            if (welcomePending) {
              setShowFirstWelcome(true);
            }
            let forceOwnerHubFromLink = false;
            try {
              forceOwnerHubFromLink =
                new URLSearchParams(window.location.search).get('ownerHub') === '1' ||
                sessionStorage.getItem(OWNER_HUB_NAV_INTENT_KEY) === '1';
            } catch {
              /* ignore */
            }
            if (ownerPath) {
              if (forceOwnerHubFromLink) {
                try {
                  sessionStorage.removeItem(OWNER_HUB_SESSION_KEY);
                  sessionStorage.removeItem(OWNER_HUB_NAV_INTENT_KEY);
                } catch {
                  /* ignore */
                }
                setOwnerHubVisible(true);
                try {
                  const qs = new URLSearchParams(window.location.search);
                  qs.delete('ownerHub');
                  const query = qs.toString();
                  const path = window.location.pathname || '/';
                  window.history.replaceState({}, '', query ? `${path}?${query}` : path);
                } catch {
                  /* ignore */
                }
              } else {
                try {
                  setOwnerHubVisible(sessionStorage.getItem(OWNER_HUB_SESSION_KEY) !== '1');
                } catch {
                  setOwnerHubVisible(true);
                }
              }
            } else {
              setOwnerHubVisible(false);
              if (forceOwnerHubFromLink) {
                try {
                  sessionStorage.removeItem(OWNER_HUB_NAV_INTENT_KEY);
                } catch {
                  /* ignore */
                }
                try {
                  const qs = new URLSearchParams(window.location.search);
                  qs.delete('ownerHub');
                  const query = qs.toString();
                  const path = window.location.pathname || '/';
                  window.history.replaceState({}, '', query ? `${path}?${query}` : path);
                } catch {
                  /* ignore */
                }
              }
            }
          } else {
            console.log('Not authenticated');
            setDevAuthShortcuts(false);
          }
        } else {
          console.log('Not authenticated');
        }
        if (!stayOnLoadingForLearning) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error checking user session:', error);
        setIsLoading(false);
      }
    };

    // Check for error in URL
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    if (errorParam === 'not_authorized') {
      setError('You must be a student in the LMS to access this application.');
    } else if (errorParam === 'auth_failed') {
      setError('Authentication failed. Please try again.');
    }

    checkUserSession();
  }, [activateBrowserDemoSession]);

  useEffect(() => {
    if (!user) {
      try {
        sessionStorage.removeItem(OWNER_HUB_SESSION_KEY);
      } catch (_) {
        /* ignore */
      }
    }
  }, [user]);

  const refreshSession = useCallback(async () => {
    try {
      const response = await fetch('/api/session', {
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      });
      if (!response.ok) return;
      const userData = await response.json();
      if (userData.loggedIn) {
        setUser(userData.user);
        setDevAuthShortcuts(Boolean(userData.devAuthShortcuts));
        setIsOwnerConsole(Boolean(userData.isOwnerConsole));
        setPersonalLicense(userData.personalLicense ?? null);
        setImpersonation(userData.impersonation?.active ? userData.impersonation : null);
        if (shouldShowFirstWelcome(userData.user?.id)) {
          setShowFirstWelcome(true);
        }
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  const handleLocalDevLicenseSkip = useCallback(() => {
    if (process.env.NODE_ENV === 'production') return;
    try {
      localStorage.setItem(SKYLITE_DEV_LICENSE_SKIP_KEY, '1');
    } catch (_) {
      /* ignore */
    }
    setDevLicenseSkipAck(true);
    try {
      sessionStorage.removeItem(OWNER_HUB_SESSION_KEY);
    } catch (_) {
      /* ignore */
    }
    setOwnerHubVisible(true);
  }, []);

  const handleDemoAccess = useCallback(() => {
    // Demo retired — open real Basic Training (requires sign-in / owner session)
    try {
      clearSkyliteDemoBrowserState();
    } catch (_) { /* ignore */ }
    window.location.href = getLearningDashboardUrl({ openDashboard: true });
  }, []);

  useEffect(() => {
    if (!user) return;
    if (readSkyliteDevLicenseSkip()) setDevLicenseSkipAck(true);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/providers', { credentials: 'include' })
      .then((r) => r.json().catch(() => ({})))
      .then((data) => {
        if (!cancelled) setAuthProviders(data);
      })
      .catch(() => {
        if (!cancelled) setAuthProviders({ google: { enabled: false }, devShortcuts: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeMemberships.length) {
      setSwitchingInstitutionId('');
      return;
    }
    const currentId = user?.institutionId;
    const hasCurrent = activeMemberships.some((m) => m.institutionId === currentId);
    if (hasCurrent) {
      setSwitchingInstitutionId(String(currentId));
      return;
    }
    setSwitchingInstitutionId(String(activeMemberships[0].institutionId));
  }, [user?.institutionId, activeMemberships]);

  useEffect(() => {
    const loadMembers = async () => {
      if (!canManageMembershipsUi || !user?.institutionId) {
        setMembers([]);
        return;
      }
      setMembersLoading(true);
      try {
        const response = await fetch('/api/institutions/active/members', {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          setMembers([]);
          return;
        }
        setMembers(Array.isArray(payload?.members) ? payload.members : []);
      } catch (_) {
        setMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };
    loadMembers();
  }, [canManageMembershipsUi, user?.institutionId, user?.activeInstitutionMembershipStatus]);

  useEffect(() => {
    const loadSeatSummary = async () => {
      if (!canManageMembershipsUi || !user?.institutionId) {
        setSeatSummary(null);
        return;
      }
      try {
        const response = await fetch('/api/institutions/active/license-summary', {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          setSeatSummary(null);
          return;
        }
        setSeatSummary(payload || null);
      } catch (_) {
        setSeatSummary(null);
      }
    };
    loadSeatSummary();
  }, [canManageMembershipsUi, user?.institutionId, user?.activeInstitutionMembershipStatus, members.length]);

  useEffect(() => {
    const loadAuditEvents = async () => {
      if (!canManageMembershipsUi || !user?.institutionId) {
        setAuditItems([]);
        setAuditHasNextPage(false);
        return;
      }
      setAuditLoading(true);
      try {
        const qs = new URLSearchParams();
        qs.set('page', String(auditPage));
        qs.set('pageSize', String(auditPageSize));
        if (auditEventTypeFilter !== 'all') qs.set('eventType', auditEventTypeFilter);
        if (auditSearch.trim()) qs.set('search', auditSearch.trim());
        const response = await fetch(`/api/institutions/active/audit-events?${qs.toString()}`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          setAuditItems([]);
          setAuditHasNextPage(false);
          return;
        }
        setAuditItems(Array.isArray(payload?.items) ? payload.items : []);
        setAuditHasNextPage(Boolean(payload?.hasNextPage));
      } catch (_) {
        setAuditItems([]);
        setAuditHasNextPage(false);
      } finally {
        setAuditLoading(false);
      }
    };
    loadAuditEvents();
  }, [canManageMembershipsUi, user?.institutionId, auditPage, auditPageSize, auditEventTypeFilter, auditSearch]);

  useEffect(() => {
    if (identityMode === 'userId') {
      setMemberTargetEmail('');
      setMemberTargetLearnerProfileId('');
      setMemberIdentifierType('');
      setMemberIdentifierValue('');
      return;
    }
    if (identityMode === 'email') {
      setMemberTargetUserId('');
      setMemberTargetLearnerProfileId('');
      setMemberIdentifierType('');
      setMemberIdentifierValue('');
      return;
    }
    if (identityMode === 'learnerProfileId') {
      setMemberTargetUserId('');
      setMemberTargetEmail('');
      setMemberIdentifierType('');
      setMemberIdentifierValue('');
      return;
    }
    if (identityMode === 'identifier') {
      setMemberTargetUserId('');
      setMemberTargetEmail('');
      setMemberTargetLearnerProfileId('');
    }
  }, [identityMode]);

  const landingGoogleSignIn = () => {
    clearSkyliteDemoBrowserState();
    window.location.href = `/auth/google?returnTo=${encodeURIComponent(POST_LOGIN_SPA_PATH)}`;
  };

  const baseZIndexes = {
    desktopIcons: 1,
    windows: 100,
    maximizedWindow: 9999,
    toolbar: 10000
  };

  useEffect(() => {
    const updateWindowConstraints = () => {
      setWindowSizes(prev => ({
        minWidth: 300,
        minHeight: 260,
        maxWidth: window.innerWidth - 24,
        // Default max height for non-browser windows; browser windows use a tighter offset.
        maxHeight: Math.max(260, window.innerHeight - DEFAULT_WINDOW_VIEWPORT_OFFSET)
      }));
    };

    window.addEventListener('resize', updateWindowConstraints);
    updateWindowConstraints();

    return () => window.removeEventListener('resize', updateWindowConstraints);
  }, []);

  // Keep normal (non-maximized) windows inside the viewport — fixes old 800px default terminals
  useEffect(() => {
    const { maxHeight, maxWidth, minHeight, minWidth } = windowSizes;
    if (maxHeight == null || maxWidth == null) return;
    setWindows((prev) =>
      prev.map((w) => {
        if (w.isMaximized) return w;
        const maxHeightForWindow = getWindowMaxHeight(w.type);
        return {
          ...w,
          size: {
            width: Math.max(minWidth ?? 300, Math.min(w.size.width, maxWidth)),
            height: Math.max(minHeight ?? 260, Math.min(w.size.height, maxHeightForWindow))
          }
        };
      })
    );
  }, [windowSizes]);

  const handleResize = (windowId, size) => {
    setWindows(prev => prev.map(w => {
      if (w.id === windowId) {
        const maxHeightForWindow = getWindowMaxHeight(w.type);
        return {
          ...w,
          size: {
            width: Math.max(Math.min(size.width, windowSizes.maxWidth), windowSizes.minWidth),
            height: Math.max(Math.min(size.height, maxHeightForWindow), windowSizes.minHeight)
          }
        };
      }
      return w;
    }));
  };

  const handleIconClick = (windowType) => {
    if (institutionAccessBlocked) return;
    if (windowType === 'license-console') {
      openOwnerLicenseConsole();
      return;
    }
    if (!allowedWindowTypes.includes(windowType)) return;
    const existingWindow = windows.find(w => w.type === windowType);
    if (existingWindow) {
      if (minimizedWindows.includes(existingWindow.id)) {
        setMinimizedWindows(prev => prev.filter(id => id !== existingWindow.id));
      }
      setActiveWindow(existingWindow.id);
      setWindows(prev => prev.map(w => ({
        ...w,
        zIndex: w.id === existingWindow.id ? 
          (w.isMaximized ? baseZIndexes.maximizedWindow : baseZIndexes.windows + zIndexCounter) : 
          w.zIndex
      })));
      setZIndexCounter(prev => prev + 1);
    } else {
      const vw = window.innerWidth;
      const maxH = getWindowMaxHeight(windowType);
      const maxW = vw - 24;
      const defaultSize =
        windowType === 'terminal'
          ? {
              width: Math.min(500, maxW),
              height: Math.min(360, maxH)
            }
          : {
              width: Math.min(600, maxW),
              height: Math.min(560, maxH)
            };
      const newWindow = {
        id: Date.now(),
        type: windowType,
        zIndex: baseZIndexes.windows + zIndexCounter,
        position: { x: 48, y: 48 },
        size: defaultSize,
        isMaximized: false,
        prevSize: null,
        prevPosition: null,
        nodeRef: React.createRef()
      };
      setWindows(prev => [...prev, newWindow]);
      setActiveWindow(newWindow.id);
      setZIndexCounter(prev => prev + 1);
    }
  };

  const handleLabBoxClick = async (box) => {
    if (institutionAccessBlocked) return;
    if (demoAccessActive) {
      window.alert('Demo mode includes the Skylite OS dashboard preview, but hackable server boxes are disabled for sales demos.');
      return;
    }
    if (!box || !box.targetUrl) return;

    if (box.id === 'box-01') {
      setBoxLaunchState((prev) => ({
        ...prev,
        [box.id]: { status: 'starting', message: t('boxLaunchPreparing') }
      }));
      setEnvironmentPreparing(true);
      try {
        await delay(BOX_START_PREP_DELAY_MS);

        setBoxLaunchState((prev) => ({
          ...prev,
          [box.id]: { status: 'starting', message: t('boxLaunchStarting') }
        }));

        const response = await fetch(BOX01_BOOT_ENDPOINT, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok) {
          throw new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
        }
        setBoxLaunchState((prev) => ({
          ...prev,
          [box.id]: { status: 'ready', message: t('boxLaunchReady') }
        }));
      } catch (error) {
        const msg = error?.message || 'Failed to start Box 01';
        setBoxLaunchState((prev) => ({
          ...prev,
          [box.id]: { status: 'error', message: t('boxLaunchStartFailed') }
        }));
        window.alert(`${t('box01ScoutStartFailed')} ${msg}`);
        return;
      } finally {
        setEnvironmentPreparing(false);
      }
    }

    localStorage.setItem(BROWSER_TARGET_STORAGE_KEY, box.targetUrl);
    window.dispatchEvent(new CustomEvent('skylite:set-browser-target', { detail: { url: box.targetUrl } }));
    handleIconClick('browser');
  };

  const handleWindowClose = (windowId) => {
    setWindows(prev => prev.filter(w => w.id !== windowId));
    setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    setActiveWindow(null);
  };

  const handleWindowClick = (windowId) => {
    if (windowId !== activeWindow) {
      setActiveWindow(windowId);
      setWindows(prev => prev.map(w => ({
        ...w,
        zIndex: w.id === windowId ? 
          (w.isMaximized ? baseZIndexes.maximizedWindow : baseZIndexes.windows + zIndexCounter) : 
          w.zIndex
      })));
      setZIndexCounter(prev => prev + 1);
    }
  };

  const handleEnterSkyliteFromLeaderHub = useCallback(() => {
    if (effectiveIsOwnerConsole && ownerInstitutionPreviewActive) {
      resetOwnerDesktopLanguage();
      clearAllOwnerPreviewSession();
      setSupportPreviewUser(null);
      setOwnerInstitutionPreviewActive(false);
      try {
        sessionStorage.setItem(OWNER_HUB_SESSION_KEY, '1');
      } catch (_) {
        /* ignore */
      }
      setOwnerHubVisible(false);
      return;
    }
    if (effectiveIsOwnerConsole) {
      resetOwnerDesktopLanguage();
    }
    persistInstitutionDesktopEntered();
    setInstitutionDesktopEntered(true);
  }, [effectiveIsOwnerConsole, ownerInstitutionPreviewActive, resetOwnerDesktopLanguage]);

  /** Full-screen license console — batch state so Skylite desktop never paints between owner hub and licenses. */
  const openOwnerLicenseConsole = useCallback(() => {
    if (!effectiveIsOwnerConsole) return;
    try {
      sessionStorage.setItem(OWNER_HUB_SESSION_KEY, '1');
    } catch (_) {
      /* ignore */
    }
    setLicenseConsolePageOpen(true);
    setOwnerHubVisible(false);
  }, [effectiveIsOwnerConsole]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let styleEl = document.getElementById('skylite-demo-privacy-style');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'skylite-demo-privacy-style';
      styleEl.textContent = DEMO_PRIVACY_CSS;
      document.head.appendChild(styleEl);
    }
    if (impersonation?.active) {
      applyDemoPrivacyBodyClass(demoPrivacyBlur);
    } else {
      applyDemoPrivacyBodyClass(false);
    }
    return undefined;
  }, [impersonation?.active, demoPrivacyBlur]);

  const handleToggleDemoPrivacyBlur = useCallback(() => {
    const next = toggleDemoPrivacyBlur();
    setDemoPrivacyBlurState(next);
  }, []);

  const handleStopImpersonation = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/impersonate/stop', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        console.warn('stop impersonation failed', data.error || r.status);
        return;
      }
      clearAllOwnerPreviewSession();
      setSupportPreviewUser(null);
      setOwnerInstitutionPreviewActive(false);
      setImpersonation(null);
      setDemoPrivacyBlur(false);
      setDemoPrivacyBlurState(false);
      try {
        clearImpersonationEnglishChrome();
        sessionStorage.removeItem(OWNER_HUB_SESSION_KEY);
        sessionStorage.setItem(OWNER_HUB_NAV_INTENT_KEY, '1');
        window.__skyliteForceEnglishChrome = true;
      } catch {
        /* ignore */
      }
      window.location.href = `${OWNER_ADMIN_HUB_PATH}?ownerHub=1`;
    } catch (err) {
      console.warn('stop impersonation network error', err);
    }
  }, []);

  const handleExitSupportPreview = useCallback(() => {
    clearAllOwnerPreviewSession();
    setSupportPreviewUser(null);
    setOwnerInstitutionPreviewActive(false);
    setOwnerHubVisible(true);
    try {
      window.history.replaceState({ ownerHub: true }, '', OWNER_ADMIN_HUB_PATH);
    } catch {
      /* ignore */
    }
  }, []);

  const handleOpenInstitutionPreviewFromOwner = useCallback((u) => {
    writeSupportPreviewUser(u);
    setSupportPreviewUser(u);
    persistOwnerInstitutionPreviewActive();
    setOwnerInstitutionPreviewActive(true);
    setOwnerHubVisible(false);
    try {
      window.history.pushState({ institutionPreview: true }, '', INSTITUTION_HUB_PATH);
    } catch {
      /* ignore */
    }
  }, []);

  const handleReturnToAnalyticsHub = useCallback(() => {
    clearInstitutionDesktopEntered();
    setInstitutionDesktopEntered(false);
  }, []);

  const handleMinimize = (windowId) => {
    setMinimizedWindows(prev => [...prev, windowId]);
  };

  const handleMaximize = (windowId) => {
    setWindows(prev => prev.map(w => {
      if (w.id === windowId) {
        if (!w.isMaximized) {
          return {
            ...w,
            isMaximized: true,
            prevSize: { width: w.size.width, height: w.size.height },
            prevPosition: w.position,
            position: { x: 0, y: 0 },
            size: {
              width: window.innerWidth,
              height: getWindowMaxHeight(w.type)
            },
            zIndex: baseZIndexes.maximizedWindow
          };
        } else {
          return {
            ...w,
            isMaximized: false,
            position: w.prevPosition,
            size: w.prevSize,
            prevSize: null,
            prevPosition: null,
            zIndex: baseZIndexes.windows + zIndexCounter
          };
        }
      }
      return w;
    }));
  };

  const getWindowDisplayName = (windowType) => {
    switch (windowType) {
      case 'terminal':
        return t('terminal').toUpperCase();
      case 'report':
        return t('riskAssessment').toUpperCase();
      case 'files':
        return t('files').toUpperCase();
      case 'browser':
        return t('browser').toUpperCase();
      default:
        return windowType.toUpperCase();
    }
  };

  const toolGroups = [
    {
      id: 'recon',
      title: '01 - Reconnaissance',
      tools: [
        { id: 'anubis', label: 'Anubus Scanner', action: () => handleIconClick('terminal') },
        { id: 'searchable', label: 'Searchable Browser', action: () => handleIconClick('browser') }
      ]
    },
    {
      id: 'reporting',
      title: '02 - Reporting',
      tools: [
        { id: 'risk', label: t('riskAssessment'), action: () => handleIconClick('report') },
        { id: 'files', label: t('files'), action: () => handleIconClick('files') }
      ]
    },
    {
      id: 'workspace',
      title: '03 - Workspace',
      tools: [
        { id: 'terminal', label: t('terminal'), action: () => handleIconClick('terminal') },
        { id: 'browser', label: t('browser'), action: () => handleIconClick('browser') }
      ]
    }
  ];
  const visibleToolGroups = toolGroups.filter((g) => shellConfig.menuGroupIds.includes(g.id));

  useEffect(() => {
    if (!visibleToolGroups.length) return;
    if (!visibleToolGroups.find((g) => g.id === activeMenuGroup)) {
      setActiveMenuGroup(visibleToolGroups[0].id);
    }
  }, [visibleToolGroups, activeMenuGroup]);

  const desktopIcons = [
    ...(effectiveIsOwnerConsole && !demoDisplayActive ? [{ type: 'license-console', icon: VpnKeyIcon, label: 'licenseConsole' }] : []),
    { type: 'terminal', icon: TerminalIcon, label: 'terminal' },
    { type: 'report', icon: ScrollIcon, label: 'riskAssessment' },
    { type: 'files', icon: FolderIcon, label: 'files' },
    { type: 'browser', icon: BugReportIcon, label: 'browser' }
  ].filter((d) => (d.type === 'license-console' ? effectiveIsOwnerConsole && !demoDisplayActive : allowedWindowTypes.includes(d.type)));

  const handleMenuToolClick = (tool) => {
    if (institutionAccessBlocked) return;
    tool.action();
    setMenuOpen(false);
  };

  const handleSwitchInstitution = async () => {
    const parsedId = parseInt(switchingInstitutionId, 10);
    if (!Number.isFinite(parsedId) || parsedId < 1) {
      setInstitutionSwitchError('Select a valid institution first.');
      return;
    }
    setInstitutionSwitchError('');
    setIsSwitchingInstitution(true);
    try {
      const response = await fetch('/api/session/active-institution', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ institutionId: parsedId })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.user) {
        setInstitutionSwitchError(payload?.error || 'Unable to switch institution.');
        return;
      }
      setUser(payload.user);
      setInstitutionSwitchError('');
    } catch (error) {
      setInstitutionSwitchError(error?.message || 'Unable to switch institution.');
    } finally {
      setIsSwitchingInstitution(false);
    }
  };

  const handleMemberStatusUpdate = async (actionType) => {
    const selectorPayload = getIdentitySelectorPayload();
    if (
      (identityMode === 'userId' && !selectorPayload.userId) ||
      (identityMode === 'email' && !selectorPayload.email) ||
      (identityMode === 'learnerProfileId' && !selectorPayload.learnerProfileId) ||
      (identityMode === 'identifier' && (!selectorPayload.identifierType || !selectorPayload.identifierValue))
    ) {
      setMemberActionMessage('Provide a valid identity selector first.');
      return;
    }

    setMemberActionLoading(true);
    setMemberActionMessage('');
    try {
      if (actionType !== 'drop' && memberTargetStatus === 'active') {
        const seatCheckResponse = await fetch('/api/institutions/active/seat-check', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ roleAtInstitution: 'student' })
        });
        const seatCheckPayload = await seatCheckResponse.json().catch(() => ({}));
        if (!seatCheckResponse.ok) {
          setMemberActionMessage(`Preflight failed: ${seatCheckPayload?.error || `HTTP ${seatCheckResponse.status}`}`);
          return;
        }
        const seatsRemaining = seatCheckPayload?.summary?.seatsRemaining;
        const shouldConfirm = seatsRemaining == null || Number(seatsRemaining) <= 1;
        if (shouldConfirm) {
          const remainingText = seatsRemaining == null ? 'unknown' : String(seatsRemaining);
          const confirmed = window.confirm(
            `Set ${getIdentitySelectorLabel()} to active? Remaining student seats after this may be limited (reported remaining: ${remainingText}).`
          );
          if (!confirmed) {
            setMemberActionMessage('Activation cancelled.');
            return;
          }
        }
      }

      const pathUserId = memberTargetUserId.trim() || '0';
      const endpoint = actionType === 'drop'
        ? `/api/institutions/active/members/${pathUserId}/drop`
        : `/api/institutions/active/members/${pathUserId}/status`;
      const body = actionType === 'drop'
        ? { ...selectorPayload }
        : { ...selectorPayload, status: memberTargetStatus };

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMemberActionMessage(`Failed: ${payload?.error || `HTTP ${response.status}`}`);
        return;
      }
      if (actionType === 'drop') {
        setMemberActionMessage(`${getIdentitySelectorLabel()} dropped (suspended). Seat released.`);
      } else {
        setMemberActionMessage(`${getIdentitySelectorLabel()} status set to ${memberTargetStatus}.`);
      }
      const refreshed = await fetch('/api/institutions/active/members', {
        credentials: 'include',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });
      const refreshedPayload = await refreshed.json().catch(() => ({}));
      if (refreshed.ok) {
        setMembers(Array.isArray(refreshedPayload?.members) ? refreshedPayload.members : []);
      }
      setAuditPage(1);
      const refreshedSummary = await fetch('/api/institutions/active/license-summary', {
        credentials: 'include',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });
      const refreshedSummaryPayload = await refreshedSummary.json().catch(() => ({}));
      if (refreshedSummary.ok) {
        setSeatSummary(refreshedSummaryPayload || null);
      }
    } catch (error) {
      setMemberActionMessage(`Failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setMemberActionLoading(false);
    }
  };

  if (isLoading || holdForLearningRedirect) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Box
          className="notranslate"
          translate="no"
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
        >
          <Typography sx={{ color: 'white' }}>{t('loading')}</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <div className="error-container notranslate" translate="no">
        <h2>{t('accessDenied')}</h2>
        <p>{error}</p>
        <button type="button" className="login-btn" onClick={() => window.location.reload()}>
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={landingTheme}>
        <CssBaseline />
        <LandingPortal authProviders={authProviders} onGoogleSignIn={landingGoogleSignIn} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      {impersonation?.active ? (
        <Box
          className="notranslate"
          translate="no"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 40000,
            px: 2,
            py: 0.85,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.25,
            bgcolor: 'rgba(120, 45, 10, 0.96)',
            borderBottom: '1px solid rgba(255, 180, 100, 0.55)',
            color: '#fff8ec'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 800, flex: '1 1 220px' }}>
            Impersonating{' '}
            <Box component="span" className={DEMO_PRIVACY_BLUR_CLASS} data-skylite-privacy="blur">
              {impersonation.targetName || impersonation.targetEmail || 'user'}
              {impersonation.targetEmail ? ` · ${impersonation.targetEmail}` : ''}
            </Box>
            {impersonation.targetRole ? ` · ${impersonation.targetRole}` : ''}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleToggleDemoPrivacyBlur}
            className="notranslate"
            translate="no"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderColor: 'rgba(255,248,236,0.65)',
              color: '#fff8ec',
              '&:hover': { borderColor: '#fff8ec', bgcolor: 'rgba(255,248,236,0.12)' }
            }}
          >
            {demoPrivacyBlur ? 'Privacy blur: On' : 'Privacy blur: Off'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleStopImpersonation}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#fff8ec', color: '#7c2d12', '&:hover': { bgcolor: '#ffe8cc' } }}
          >
            Exit impersonation
          </Button>
        </Box>
      ) : null}
      {user &&
      !showLicenseGate &&
      !effectiveIsOwnerConsole &&
      personalLicense?.status === 'active' &&
      personalLicense?.isTrial ? (
        <Box
          className="notranslate skylite-trial-banner"
          translate="no"
          dir="ltr"
          lang="en"
          role="status"
          sx={{
            position: 'fixed',
            top: impersonation?.active ? 48 : 0,
            left: 0,
            right: 0,
            zIndex: 39990,
            px: 2,
            py: 0.85,
            textAlign: 'center',
            bgcolor: '#ffe566',
            borderBottom: '1px solid #e6c200',
            color: '#c62828'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#c62828', letterSpacing: 0.2 }}>
            Welcome to your 7 day trial account
          </Typography>
        </Box>
      ) : null}
      {showLicenseGate ? (
        <LicenseGateOverlay
          personalLicense={licenseGateOverlayProps.personalLicense}
          onRedeemed={() => {
            setLicenseGatePreviewMode(null);
            try {
              clearRetiredDemoStorage();
            } catch (_) {
              /* ignore */
            }
            const uid = user?.id;
            if (uid) {
              markFirstWelcomePending(uid);
              setShowFirstWelcome(true);
            }
            refreshSession();
          }}
          onClosePreview={licenseGateOverlayProps.onClosePreview}
          onRequestLocalDevLicenseSkip={
            process.env.NODE_ENV !== 'production' ? handleLocalDevLicenseSkip : undefined
          }
        />
      ) : null}
      {showFirstWelcome && user && !showLicenseGate ? (
        <FirstWelcomeOverlay
          onContinue={() => {
            markFirstWelcomeSeen(user.id);
            const roleLc = String(user.role || 'student').toLowerCase();
            if (
              roleLc === 'student' &&
              !effectiveIsOwnerConsole &&
              !demoDisplayActive
            ) {
              setHoldForLearningRedirect(true);
              window.location.replace(getLearningDashboardUrl({ openDashboard: true }));
              return;
            }
            setShowFirstWelcome(false);
          }}
        />
      ) : null}
      {user ? (
        <Box
          className="skylite-owner-account-strip notranslate"
          translate="no"
          dir="ltr"
          lang="en"
          sx={{
            position: 'fixed',
            top:
              (impersonation?.active ? 52 : 0) +
                (!showLicenseGate &&
                !effectiveIsOwnerConsole &&
                personalLicense?.status === 'active' &&
                personalLicense?.isTrial
                  ? 44
                  : 0) +
                18,
            right: 20,
            zIndex: licenseConsolePageOpen ? 27000 : 10002,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.75,
            maxWidth: { xs: 'min(calc(100vw - 40px), 92vw)', sm: 'min(640px, 92vw)' }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'nowrap',
              justifyContent: 'flex-end'
            }}
          >
            <Typography
              variant="body2"
              noWrap
              title={accountStripDisplayTitle}
              className={DEMO_PRIVACY_BLUR_CLASS}
              data-skylite-privacy="blur"
              sx={{
                color: 'rgba(232, 247, 255, 0.95)',
                fontWeight: 700,
                fontSize: '0.82rem',
                letterSpacing: '0.02em',
                textShadow: '0 1px 4px rgba(0,0,0,0.75)'
              }}
            >
              {accountStripDisplayLabel}
            </Typography>
            <Button
              size="small"
              variant="contained"
              disabled={demoDisplayActive}
              title={demoDisplayActive ? 'My Dashboard is disabled in demo mode' : undefined}
              onClick={() => {
                if (!effectiveIsOwnerConsole) {
                  window.location.href = getLearningDashboardUrl({ openDashboard: true });
                  return;
                }
                if (ownerInstitutionPreviewActive && supportPreviewUser) {
                  handleExitSupportPreview();
                  return;
                }
                if (licenseConsolePageOpen) {
                  setLicenseConsolePageOpen(false);
                  setOwnerHubVisible(true);
                  try {
                    window.dispatchEvent(new Event('commandline-close-marketplace'));
                  } catch (_) { /* ignore */ }
                  return;
                }
                try {
                  sessionStorage.removeItem(OWNER_HUB_SESSION_KEY);
                } catch (_) {
                  /* ignore */
                }
                try {
                  window.dispatchEvent(new Event('commandline-close-marketplace'));
                } catch (_) { /* ignore */ }
                setOwnerHubVisible(true);
                try {
                  window.history.replaceState({ ownerHub: true }, '', OWNER_ADMIN_HUB_PATH);
                } catch {
                  /* ignore */
                }
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.72rem',
                py: 0.35,
                px: 1,
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                background: 'linear-gradient(135deg, rgba(60, 130, 220, 0.95), rgba(30, 80, 180, 0.98))',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(80, 150, 240, 1), rgba(40, 100, 200, 1))'
                },
                '&.Mui-disabled': {
                  color: 'rgba(220, 245, 255, 0.55)',
                  background: 'rgba(90, 110, 130, 0.45)',
                  boxShadow: 'none',
                  cursor: 'not-allowed',
                  pointerEvents: 'auto'
                }
              }}
            >
              {demoDisplayActive ? 'My Dashboard' : (effectiveIsOwnerConsole ? t('ownerStripOpenHub') : 'My Dashboard')}
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                if (demoDisplayActive) clearSkyliteDemoBrowserState();
                window.location.href = '/logout';
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.72rem',
                color: 'rgba(220, 245, 255, 0.92)',
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(120, 200, 255, 0.12)' }
              }}
            >
              {t('ownerLogOut')}
            </Button>
            <LanguageSelector variant="inline" surface="dark" />
          </Box>
          <Button
            size="small"
            variant="outlined"
            className="notranslate"
            translate="no"
            onClick={() => {
              setSupportTicketMode('submit');
              setSupportTicketOpen(true);
            }}
            title="Report a problem or recommendation"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.72rem',
              py: 0.35,
              px: 1.25,
              flexShrink: 0,
              borderRadius: '999px',
              color: '#dff6ff',
              borderColor: 'rgba(140, 210, 255, 0.55)',
              backgroundColor: 'rgba(6, 12, 28, 0.55)',
              '&:hover': { borderColor: 'rgba(180, 230, 255, 0.85)', backgroundColor: 'rgba(80, 160, 255, 0.12)' }
            }}
          >
            Report Issue
          </Button>
        </Box>
      ) : null}
      {showInstitutionLeaderHub ? (
        <Box
          sx={{
            height: '100vh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxSizing: 'border-box',
            pt: '52px'
          }}
        >
          {effectiveIsOwnerConsole && supportPreviewUser ? (
            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 2, sm: 3 },
                py: 0.75,
                borderBottom: '1px solid rgba(120, 200, 255, 0.18)',
                bgcolor: 'rgba(6, 10, 22, 0.92)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={handleExitSupportPreview}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                ← Admin Home
              </Button>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                Previewing {supportPreviewUser.name} (live classes, read-only) · your owner session stays signed in
              </Typography>
            </Box>
          ) : null}
          <InstitutionLeaderDashboard
            sessionRole={String(supportPreviewUser?.roleAtInstitution || sessionRole).toLowerCase()}
            institutionName={supportPreviewUser?.institutionName || activeInstitutionName}
            previewUser={
              effectiveIsOwnerConsole && supportPreviewUser ? supportPreviewUser : null
            }
            canImpersonateStudents={Boolean(impersonation?.active || effectiveIsOwnerConsole)}
            onEnterSkylite={handleEnterSkyliteFromLeaderHub}
            onExitSupportPreview={
              effectiveIsOwnerConsole && supportPreviewUser
                ? handleExitSupportPreview
                : undefined
            }
          />
        </Box>
      ) : effectiveIsOwnerConsole && !demoDisplayActive && ownerHubVisible && !showLicenseGate ? (
        <Box
          sx={{
            height: '100vh',
            maxHeight: '100dvh',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            boxSizing: 'border-box',
            pt: '52px'
          }}
        >
          <OwnerAdminDashboard
            trainingUrl={getLearningDashboardUrl({ openDashboard: true })}
            onEnterSkyliteDesktop={() => {
              resetOwnerDesktopLanguage();
              try {
                sessionStorage.setItem(OWNER_HUB_SESSION_KEY, '1');
              } catch (_) {
                /* ignore */
              }
              setOwnerHubVisible(false);
            }}
            onOpenLicenses={openOwnerLicenseConsole}
            onOpenSupportTickets={() => {
              setSupportTicketMode('admin');
              setSupportTicketOpen(true);
            }}
          />
        </Box>
      ) : licenseConsolePageOpen ? null : (
      <Box 
        className="desktop-background notranslate"
        translate="no"
        dir="ltr"
        lang="en"
        sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          overflow: 'hidden'
        }}
      >
        {institutionAccessBlocked ? (
          <Box
            sx={{
              position: 'fixed',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 21000,
              px: 2,
              py: 1,
              borderRadius: 1.5,
              border: '1px solid rgba(255, 182, 138, 0.65)',
              backgroundColor: 'rgba(66, 22, 5, 0.92)',
              boxShadow: '0 8px 22px rgba(0, 0, 0, 0.4)',
              maxWidth: 'min(900px, calc(100vw - 32px))'
            }}
          >
            <Typography sx={{ color: '#ffd9c2', fontWeight: 700, fontSize: '0.9rem', textAlign: 'center', mb: activeMemberships.length ? 1 : 0 }}>
              {institutionAccessMessage}
            </Typography>
            {activeMemberships.length ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="switch-institution-label" sx={{ color: 'rgba(255, 217, 194, 0.9)' }}>
                    Switch institution
                  </InputLabel>
                  <Select
                    labelId="switch-institution-label"
                    value={switchingInstitutionId}
                    label="Switch institution"
                    onChange={(e) => setSwitchingInstitutionId(String(e.target.value))}
                    disabled={isSwitchingInstitution}
                    sx={{
                      color: '#fff1e6',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 200, 170, 0.55)' },
                      '& .MuiSvgIcon-root': { color: '#ffd9c2' }
                    }}
                  >
                    {activeMemberships.map((m) => (
                      <MenuItem key={m.institutionId} value={String(m.institutionId)}>
                        {m.institutionName || `Institution ${m.institutionId}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleSwitchInstitution}
                  disabled={isSwitchingInstitution}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {isSwitchingInstitution ? 'Switching...' : 'Switch'}
                </Button>
              </Box>
            ) : null}
            {institutionSwitchError ? (
              <Typography sx={{ color: '#ffd0d0', mt: 0.75, textAlign: 'center', fontSize: '0.78rem' }}>
                {institutionSwitchError}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {shellPreviewActive && !licenseConsolePageOpen ? (
          <Box
            sx={{
              position: 'fixed',
              top: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 20998,
              pointerEvents: 'auto'
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`Shell preview: ${shellPreviewActive} (signed in as ${sessionRole})`}
              onDelete={() => applyShellPreview('')}
              sx={{
                borderColor: 'rgba(255, 206, 132, 0.75)',
                color: '#fff5e0',
                backgroundColor: 'rgba(48, 28, 6, 0.88)',
                '& .MuiChip-deleteIcon': { color: 'rgba(255, 220, 180, 0.9)' }
              }}
            />
          </Box>
        ) : null}
        {canManageMembershipsUi && !effectiveIsOwnerConsole && !licenseConsolePageOpen ? (
          <Box
            sx={{
              position: 'fixed',
              top: institutionAccessBlocked ? 128 : 12,
              right: 12,
              zIndex: 20500,
              width: 340,
              maxWidth: 'calc(100vw - 24px)',
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid rgba(150, 220, 255, 0.45)',
              background: 'linear-gradient(180deg, rgba(10, 22, 44, 0.92) 0%, rgba(7, 16, 34, 0.94) 100%)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)'
            }}
          >
            <Typography sx={{ color: '#d8f3ff', fontWeight: 700, fontSize: '0.82rem', mb: 1 }}>
              Membership Controls (Teacher/Admin)
            </Typography>
            {canShellPreview ? (
              <Box sx={{ mb: 1 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="shell-preview-label" sx={{ color: 'rgba(200, 238, 255, 0.85)' }}>
                    Preview Skylite shell
                  </InputLabel>
                  <Select
                    labelId="shell-preview-label"
                    value={shellPreviewActive || '__session__'}
                    label="Preview Skylite shell"
                    onChange={(e) => {
                      const v = String(e.target.value);
                      applyShellPreview(v === '__session__' ? '' : v);
                    }}
                    sx={{
                      color: '#e5f6ff',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(170, 225, 255, 0.45)' },
                      '& .MuiSvgIcon-root': { color: 'rgba(200, 238, 255, 0.85)' }
                    }}
                  >
                    <MenuItem value="__session__">Session role ({sessionRole})</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="teacher">Teacher</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="tester">Tester</MenuItem>
                  </Select>
                </FormControl>
                <Typography sx={{ color: 'rgba(180, 210, 230, 0.75)', fontSize: '0.65rem', mt: 0.5 }}>
                  UI only; APIs still use your signed-in role.
                </Typography>
              </Box>
            ) : null}
            <Typography sx={{ color: seatHealthColor, fontSize: '0.72rem', mb: 1, fontWeight: 700 }}>
              {seatHealthText}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Search"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="name, email, id..."
                sx={{
                  flex: 1,
                  '& .MuiInputLabel-root': { color: 'rgba(200, 238, 255, 0.85)' },
                  '& .MuiOutlinedInput-root': {
                    color: '#e5f6ff',
                    '& fieldset': { borderColor: 'rgba(170, 225, 255, 0.45)' }
                  }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 112 }}>
                <InputLabel id="member-status-filter-label" sx={{ color: 'rgba(200, 238, 255, 0.85)' }}>
                  Filter
                </InputLabel>
                <Select
                  labelId="member-status-filter-label"
                  value={memberStatusFilter}
                  label="Filter"
                  onChange={(e) => setMemberStatusFilter(String(e.target.value))}
                  sx={{
                    color: '#e5f6ff',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(170, 225, 255, 0.45)' },
                    '& .MuiSvgIcon-root': { color: '#bde9ff' }
                  }}
                >
                  <MenuItem value="all">all</MenuItem>
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="invited">invited</MenuItem>
                  <MenuItem value="suspended">suspended</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="identity-mode-label" sx={{ color: 'rgba(200, 238, 255, 0.85)' }}>
                  Identity
                </InputLabel>
                <Select
                  labelId="identity-mode-label"
                  value={identityMode}
                  label="Identity"
                  onChange={(e) => setIdentityMode(String(e.target.value))}
                  sx={{
                    color: '#e5f6ff',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(170, 225, 255, 0.45)' },
                    '& .MuiSvgIcon-root': { color: '#bde9ff' }
                  }}
                >
                  <MenuItem value="userId">user id</MenuItem>
                  <MenuItem value="email">email</MenuItem>
                  <MenuItem value="learnerProfileId">learner profile</MenuItem>
                  <MenuItem value="identifier">identifier pair</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ color: 'rgba(190, 226, 245, 0.82)', fontSize: '0.68rem', minWidth: 140, alignSelf: 'center' }}>
                {identityMode === 'userId'
                  ? 'Use direct internal user id'
                  : identityMode === 'email'
                    ? 'Resolve by exact account email'
                    : identityMode === 'learnerProfileId'
                      ? 'Resolve by portable learner profile id'
                      : 'Resolve by identifierType + identifierValue (SIS/SSO/etc.)'}
              </Typography>
              {identityMode === 'userId' ? (
              <TextField
                size="small"
                label="User ID"
                value={memberTargetUserId}
                onChange={(e) => setMemberTargetUserId(e.target.value.replace(/[^\d]/g, ''))}
                sx={{
                  minWidth: 96,
                  flex: '1 1 96px',
                  '& .MuiInputLabel-root': { color: 'rgba(200, 238, 255, 0.85)' },
                  '& .MuiOutlinedInput-root': {
                    color: '#e5f6ff',
                    '& fieldset': { borderColor: 'rgba(170, 225, 255, 0.45)' }
                  }
                }}
              />
              ) : null}
              {identityMode === 'email' ? (
                <TextField
                  size="small"
                  label="Email"
                  value={memberTargetEmail}
                  onChange={(e) => setMemberTargetEmail(e.target.value)}
                  sx={{
                    minWidth: 180,
                    flex: '1 1 180px',
                    '& .MuiInputLabel-root': { color: 'rgba(200, 238, 255, 0.85)' },
                    '& .MuiOutlinedInput-root': {
                      color: '#e5f6ff',
                      '& fieldset': { borderColor: 'rgba(170, 225, 255, 0.45)' }
                    }
                  }}
                />
              ) : null}
              {identityMode === 'learnerProfileId' ? (
                <TextField
                  size="small"
                  label="Learner Profile ID"
                  value={memberTargetLearnerProfileId}
                  onChange={(e) => setMemberTargetLearnerProfileId(e.target.value.replace(/[^\d]/g, ''))}
                  sx={{
                    minWidth: 160,
                    flex: '1 1 160px',
                    '& .MuiInputLabel-root': { color: 'rgba(200, 238, 255, 0.85)' },
                    '& .MuiOutlinedInput-root': {
                      color: '#e5f6ff',
                      '& fieldset': { borderColor: 'rgba(170, 225, 255, 0.45)' }
                    }
                  }}
                />
              ) : null}
              {identityMode === 'identifier' ? (
                <>
                  <TextField
                    size="small"
                    label="Identifier Type"
                    value={memberIdentifierType}
                    onChange={(e) => setMemberIdentifierType(e.target.value)}
                    sx={{
                      minWidth: 140,
                      flex: '1 1 140px',
                      '& .MuiInputLabel-root': { color: 'rgba(200, 238, 255, 0.85)' },
                      '& .MuiOutlinedInput-root': {
                        color: '#e5f6ff',
                        '& fieldset': { borderColor: 'rgba(170, 225, 255, 0.45)' }
                      }
                    }}
                  />
                  <TextField
                    size="small"
                    label="Identifier Value"
                    value={memberIdentifierValue}
                    onChange={(e) => setMemberIdentifierValue(e.target.value)}
                    sx={{
                      minWidth: 160,
                      flex: '1 1 160px',
                      '& .MuiInputLabel-root': { color: 'rgba(200, 238, 255, 0.85)' },
                      '& .MuiOutlinedInput-root': {
                        color: '#e5f6ff',
                        '& fieldset': { borderColor: 'rgba(170, 225, 255, 0.45)' }
                      }
                    }}
                  />
                </>
              ) : null}
              <FormControl size="small" sx={{ minWidth: 120, flex: '1 1 120px' }}>
                <InputLabel id="member-status-label" sx={{ color: 'rgba(200, 238, 255, 0.85)' }}>
                  Status
                </InputLabel>
                <Select
                  labelId="member-status-label"
                  value={memberTargetStatus}
                  label="Status"
                  onChange={(e) => setMemberTargetStatus(String(e.target.value))}
                  sx={{
                    color: '#e5f6ff',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(170, 225, 255, 0.45)' },
                    '& .MuiSvgIcon-root': { color: '#bde9ff' }
                  }}
                >
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="suspended">suspended</MenuItem>
                  <MenuItem value="invited">invited</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {getIdentitySelectorLabel() && getIdentitySelectorLabel() !== 'User ' ? (
              <Typography sx={{ color: '#bfe8ff', mt: 0.8, fontSize: '0.74rem' }}>
                Selected: {getIdentitySelectorLabel()}
              </Typography>
            ) : (
              <Typography sx={{ color: 'rgba(191, 232, 255, 0.68)', mt: 0.8, fontSize: '0.74rem' }}>
                Selected: none
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleMemberStatusUpdate('status')}
                disabled={memberActionLoading}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Set Status
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => {
                  const label = getIdentitySelectorLabel();
                  if (!label || label === 'User ') {
                    setMemberActionMessage('Provide a valid identity selector before dropping.');
                    return;
                  }
                  const confirmed = window.confirm(`Drop (suspend) ${label}? This will release the seat immediately.`);
                  if (!confirmed) return;
                  handleMemberStatusUpdate('drop');
                }}
                disabled={memberActionLoading}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Drop (Suspend)
              </Button>
            </Box>
            {memberActionMessage ? (
              <Typography sx={{ color: '#cfeeff', mt: 0.8, fontSize: '0.75rem' }}>
                {memberActionMessage}
              </Typography>
            ) : null}
            <TableContainer
              component={Paper}
              sx={{
                mt: 1,
                maxHeight: 180,
                backgroundColor: 'rgba(8, 18, 34, 0.72)',
                border: '1px solid rgba(120, 200, 255, 0.2)'
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
                      <TableSortLabel
                        active={memberSortBy === 'user'}
                        direction={memberSortBy === 'user' ? memberSortDir : 'asc'}
                        onClick={() => handleSortMembers('user')}
                      >
                        User
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
                      <TableSortLabel
                        active={memberSortBy === 'role'}
                        direction={memberSortBy === 'role' ? memberSortDir : 'asc'}
                        onClick={() => handleSortMembers('role')}
                      >
                        Role
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
                      <TableSortLabel
                        active={memberSortBy === 'status'}
                        direction={memberSortBy === 'status' ? memberSortDir : 'asc'}
                        onClick={() => handleSortMembers('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {membersLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontSize: '0.72rem' }}>Loading members...</TableCell>
                    </TableRow>
                  ) : visibleMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontSize: '0.72rem' }}>No matching members.</TableCell>
                    </TableRow>
                  ) : visibleMembers.map((m) => (
                    <TableRow
                      key={`${m.InstitutionId}-${m.UserId}`}
                      hover
                      onClick={() => {
                        setIdentityMode('userId');
                        setMemberTargetUserId(String(m.UserId));
                        setMemberTargetEmail(String(m.UserEmail || ''));
                        setMemberTargetStatus(String(m.Status || 'suspended'));
                      }}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: String(memberTargetUserId) === String(m.UserId)
                          ? 'rgba(120, 205, 255, 0.2)'
                          : 'transparent',
                        '& td': { fontSize: '0.7rem' }
                      }}
                    >
                      <TableCell>{m.UserName || m.UserEmail || `User ${m.UserId}`}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={m.RoleAtInstitution}
                          sx={{
                            height: 20,
                            fontSize: '0.64rem',
                            fontWeight: 700,
                            backgroundColor:
                              String(m.RoleAtInstitution || '').toLowerCase() === 'admin'
                                ? 'rgba(190, 130, 255, 0.22)'
                                : String(m.RoleAtInstitution || '').toLowerCase() === 'teacher'
                                  ? 'rgba(110, 185, 255, 0.22)'
                                  : String(m.RoleAtInstitution || '').toLowerCase() === 'student'
                                    ? 'rgba(130, 220, 170, 0.22)'
                                    : 'rgba(140, 180, 220, 0.22)',
                            color:
                              String(m.RoleAtInstitution || '').toLowerCase() === 'admin'
                                ? '#e8d3ff'
                                : String(m.RoleAtInstitution || '').toLowerCase() === 'teacher'
                                  ? '#cfe9ff'
                                  : String(m.RoleAtInstitution || '').toLowerCase() === 'student'
                                    ? '#cdf6dd'
                                    : '#d3e8f8',
                            border: '1px solid rgba(200, 230, 255, 0.25)'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={m.Status}
                          sx={{
                            height: 20,
                            fontSize: '0.64rem',
                            fontWeight: 700,
                            backgroundColor:
                              String(m.Status || '').toLowerCase() === 'active'
                                ? 'rgba(90, 190, 120, 0.22)'
                                : String(m.Status || '').toLowerCase() === 'invited'
                                  ? 'rgba(255, 190, 85, 0.22)'
                                  : String(m.Status || '').toLowerCase() === 'suspended'
                                    ? 'rgba(255, 120, 130, 0.22)'
                                    : 'rgba(140, 180, 220, 0.22)',
                            color:
                              String(m.Status || '').toLowerCase() === 'active'
                                ? '#baf7c7'
                                : String(m.Status || '').toLowerCase() === 'invited'
                                  ? '#ffe1ad'
                                  : String(m.Status || '').toLowerCase() === 'suspended'
                                    ? '#ffc7cd'
                                    : '#d3e8f8',
                            border: '1px solid rgba(200, 230, 255, 0.25)'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 0.9, display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
              <Chip size="small" label="Role: student" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(130, 220, 170, 0.22)', color: '#cdf6dd' }} />
              <Chip size="small" label="Role: teacher" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(110, 185, 255, 0.22)', color: '#cfe9ff' }} />
              <Chip size="small" label="Role: admin" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(190, 130, 255, 0.22)', color: '#e8d3ff' }} />
              <Chip size="small" label="Status: active" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(90, 190, 120, 0.22)', color: '#baf7c7' }} />
              <Chip size="small" label="Status: invited" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(255, 190, 85, 0.22)', color: '#ffe1ad' }} />
              <Chip size="small" label="Status: suspended" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(255, 120, 130, 0.22)', color: '#ffc7cd' }} />
            </Box>

            <Box sx={{ mt: 1.4, borderTop: '1px solid rgba(140, 210, 255, 0.18)', pt: 1 }}>
              <Typography sx={{ color: '#d8f3ff', fontWeight: 700, fontSize: '0.78rem', mb: 0.8 }}>
                Audit Events
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="audit-event-type-filter-label" sx={{ color: 'rgba(200, 238, 255, 0.85)' }}>
                    Event
                  </InputLabel>
                  <Select
                    labelId="audit-event-type-filter-label"
                    value={auditEventTypeFilter}
                    label="Event"
                    onChange={(e) => {
                      setAuditEventTypeFilter(String(e.target.value));
                      setAuditPage(1);
                    }}
                    sx={{
                      color: '#e5f6ff',
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(170, 225, 255, 0.45)' },
                      '& .MuiSvgIcon-root': { color: '#bde9ff' }
                    }}
                  >
                    <MenuItem value="all">all events</MenuItem>
                    <MenuItem value="institution.member.added_or_updated">member add/update</MenuItem>
                    <MenuItem value="institution.member.status.updated">member status update</MenuItem>
                    <MenuItem value="institution.member.dropped">member dropped</MenuItem>
                    <MenuItem value="session.active_institution.switched">institution switch</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Search"
                  value={auditSearch}
                  onChange={(e) => {
                    setAuditSearch(e.target.value);
                    setAuditPage(1);
                  }}
                  placeholder="event or payload..."
                  sx={{
                    flex: 1,
                    '& .MuiInputLabel-root': { color: 'rgba(200, 238, 255, 0.85)' },
                    '& .MuiOutlinedInput-root': {
                      color: '#e5f6ff',
                      '& fieldset': { borderColor: 'rgba(170, 225, 255, 0.45)' }
                    }
                  }}
                />
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 180,
                  backgroundColor: 'rgba(8, 18, 34, 0.72)',
                  border: '1px solid rgba(120, 200, 255, 0.2)'
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700 }}>Time</TableCell>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700 }}>Event</TableCell>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700 }}>Actor</TableCell>
                      <TableCell sx={{ fontSize: '0.66rem', fontWeight: 700 }}>Summary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ fontSize: '0.7rem' }}>Loading audit events...</TableCell>
                      </TableRow>
                    ) : auditItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ fontSize: '0.7rem' }}>No audit events found.</TableCell>
                      </TableRow>
                    ) : auditItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell sx={{ fontSize: '0.68rem' }}>
                          {item.occurredAt ? new Date(item.occurredAt).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.68rem' }}>{item.eventType}</TableCell>
                        <TableCell sx={{ fontSize: '0.68rem' }}>
                          {item.actorName || item.actorEmail || (item.actorUserId ? `User ${item.actorUserId}` : '-')}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.68rem' }}>
                          {summarizeAuditEvent(item)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.6 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                  disabled={auditPage <= 1 || auditLoading}
                  sx={{ textTransform: 'none' }}
                >
                  Prev
                </Button>
                <Typography sx={{ color: 'rgba(200, 236, 255, 0.85)', fontSize: '0.68rem' }}>
                  Page {auditPage}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setAuditPage((p) => p + 1)}
                  disabled={!auditHasNextPage || auditLoading}
                  sx={{ textTransform: 'none' }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        ) : null}
        {environmentPreparing ? (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 20000,
              backgroundColor: 'rgba(3, 10, 24, 0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2px)'
            }}
          >
            <Box
              sx={{
                minWidth: 320,
                maxWidth: 420,
                px: 3,
                py: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(122, 208, 255, 0.35)',
                background: 'linear-gradient(180deg, rgba(10, 22, 44, 0.94) 0%, rgba(7, 14, 30, 0.96) 100%)',
                boxShadow: '0 12px 28px rgba(0, 0, 0, 0.45)',
                textAlign: 'center'
              }}
            >
              <CircularProgress size={42} sx={{ color: '#8bd5ff', mb: 1.5 }} />
              <Typography sx={{ color: '#e8f7ff', fontWeight: 700, letterSpacing: '0.03em' }}>
                Environment Being Prepared
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(212, 241, 255, 0.82)', display: 'block', mt: 0.8 }}>
                Please wait while services come online...
              </Typography>
            </Box>
          </Box>
        ) : null}
        <Box className="desktop-skylite-hero" aria-hidden="true">
          <Box className="desktop-skylite-glass-cube-scene">
            <Box className="desktop-skylite-glass-cube desktop-skylite-glass-cube--solved">
              <Box className="desktop-skylite-glass-cube-spin">
                <Box className="desktop-glass-cube-face face-front">
                  <Box className="desktop-glass-cube-solved-label">
                    <span className="desktop-cube-solved-line">sky</span>
                    <span className="desktop-cube-solved-line desktop-cube-solved-line-lite">LITE</span>
                  </Box>
                </Box>
                <Box className="desktop-glass-cube-face face-back">
                  <Box className="desktop-glass-cube-solved-label">
                    <span className="desktop-cube-solved-line">sky</span>
                    <span className="desktop-cube-solved-line desktop-cube-solved-line-lite">LITE</span>
                  </Box>
                </Box>
                <Box className="desktop-glass-cube-face face-right">
                  <Box className="desktop-glass-cube-solved-label">
                    <span className="desktop-cube-solved-line">sky</span>
                    <span className="desktop-cube-solved-line desktop-cube-solved-line-lite">LITE</span>
                  </Box>
                </Box>
                <Box className="desktop-glass-cube-face face-left">
                  <Box className="desktop-glass-cube-solved-label">
                    <span className="desktop-cube-solved-line">sky</span>
                    <span className="desktop-cube-solved-line desktop-cube-solved-line-lite">LITE</span>
                  </Box>
                </Box>
                <Box className="desktop-glass-cube-face face-top">
                  <Box className="desktop-glass-cube-solved-label">
                    <span className="desktop-cube-solved-line">sky</span>
                    <span className="desktop-cube-solved-line desktop-cube-solved-line-lite">LITE</span>
                  </Box>
                </Box>
                <Box className="desktop-glass-cube-face face-bottom">
                  <Box className="desktop-glass-cube-solved-label">
                    <span className="desktop-cube-solved-line">sky</span>
                    <span className="desktop-cube-solved-line desktop-cube-solved-line-lite">LITE</span>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Left slide-out: lab boxes grid (expand / collapse) */}
        <Box
          className="skylite-left-panel"
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 30,
            width: leftPanelExpanded ? LEFT_PANEL_EXPANDED_W : LEFT_PANEL_RAIL_W,
            zIndex: 120,
            transition: 'width 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRight: '1px solid rgba(124, 210, 255, 0.28)',
            background: 'linear-gradient(180deg, rgba(6, 12, 26, 0.96) 0%, rgba(10, 20, 42, 0.94) 100%)',
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.35)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {!leftPanelExpanded && (
            <IconButton
              onClick={() => setLeftPanelExpanded(true)}
              aria-label={t('expandLabBoxesPanel')}
              sx={{
                flex: 1,
                width: '100%',
                minHeight: 120,
                borderRadius: 0,
                color: '#8ed9ff',
                flexDirection: 'column',
                gap: 0.5,
                '&:hover': { backgroundColor: 'rgba(80, 160, 220, 0.12)' }
              }}
            >
              <ChevronRight sx={{ fontSize: 22 }} />
            </IconButton>
          )}
          {leftPanelExpanded && (
            <>
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  px: 1,
                  py: 0.75,
                  borderBottom: '1px solid rgba(124, 210, 255, 0.2)',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#d4f1ff',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                >
                  {t('labBoxes')}
                </Typography>
                <IconButton
                  size="small"
                  onClick={collapseLeftPanel}
                  aria-label={t('collapseLabBoxesPanel')}
                  sx={{ color: '#9ee0ff', p: 0.35 }}
                >
                  <ChevronLeft sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  px: 1,
                  py: 1,
                  '&::-webkit-scrollbar': { width: 7 },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(120, 200, 255, 0.35)',
                    borderRadius: 4
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 1,
                    alignContent: 'start'
                  }}
                >
                  {LAB_BOX_PLACEHOLDERS.map((box) => (
                    <LabBoxTile
                      key={box.id}
                      labelKey={box.labelKey}
                      disabled={
                        institutionAccessBlocked ||
                        demoAccessActive ||
                        (box.id === 'box-01' && boxLaunchState[box.id]?.status === 'starting')
                      }
                      statusText={
                        demoAccessActive
                          ? 'Demo preview only'
                          : box.id === 'box-01'
                            ? (boxLaunchState[box.id]?.message || '')
                            : ''
                      }
                      onClick={() => handleLabBoxClick(box)}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>
      
        <Box
          className="desktop-icons"
        sx={{
          position: 'absolute',
          top: 20,
          left: 12 + leftPanelOffset,
          display: 'flex',
          flexDirection: 'column',
            zIndex: baseZIndexes.desktopIcons
        }}
      >
        {desktopIcons.map((item) => (
          <DesktopIcon
            key={item.type}
            icon={item.icon}
            label={item.label}
            onClick={() => handleIconClick(item.type)}
          />
        ))}
      </Box>

      {windows.map(window => !minimizedWindows.includes(window.id) && (
        <Draggable
          key={window.id}
          handle=".window-header"
          nodeRef={window.nodeRef}
          defaultPosition={window.position}
          position={window.isMaximized ? { x: 0, y: 0 } : null}
          bounds="parent"
          disabled={window.isMaximized}
          onDrag={(e, data) => {
            setWindows(prev => prev.map(w => 
              w.id === window.id ? { ...w, position: { x: data.x, y: data.y } } : w
            ));
          }}
        >
          <Box
            ref={window.nodeRef}
            sx={{
              position: 'absolute',
              display: 'inline-block',
              zIndex: window.zIndex
            }}
              onClick={() => handleWindowClick(window.id)}
          >
            <Resizable
              width={window.size.width}
              height={window.size.height}
              minConstraints={[windowSizes.minWidth, windowSizes.minHeight]}
              maxConstraints={[windowSizes.maxWidth, getWindowMaxHeight(window.type)]}
              onResize={(e, { size }) => handleResize(window.id, size)}
                resizeHandles={['se']}
            >
              <Box
                className="window"
                data-type={window.type}
                data-maximized={window.isMaximized}
                sx={{
                    width: window.size.width,
                    height: window.size.height,
                    borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
              >
                <Box
                  className="window-header"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                    cursor: 'move',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'white', flex: 1, letterSpacing: '0.08em' }}>{getWindowDisplayName(window.type)}</Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    '& > *': {
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      color: 'rgba(255, 255, 255, 0.8)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: 'white',
                        transform: 'scale(1.1)',
                      }
                    }
                  }}>
                    <MinimizeIcon onClick={(e) => {
                      e.stopPropagation();
                      handleMinimize(window.id);
                    }} />
                    <MaximizeIcon onClick={(e) => {
                      e.stopPropagation();
                      handleMaximize(window.id);
                    }} />
                    <CloseIcon onClick={(e) => {
                      e.stopPropagation();
                      handleWindowClose(window.id);
                    }} />
                  </Box>
                </Box>
                <Box sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: window.type === 'terminal' ? 'hidden' : 'auto',
                  display: window.type === 'terminal' ? 'flex' : undefined,
                  flexDirection: window.type === 'terminal' ? 'column' : undefined,
                  p: window.type === 'browser' ? 0 : 1
                }}>
                {window.type === 'terminal' && <Terminal ref={terminalRef} />}
                {window.type === 'report' && <ReportSummary />}
                {window.type === 'files' && <Files />}
                {window.type === 'browser' && <Browser />}
                </Box>
              </Box>
            </Resizable>
          </Box>
        </Draggable>
      ))}

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '30px',
          background: 'linear-gradient(90deg, rgba(5, 10, 22, 0.95), rgba(10, 20, 40, 0.92))',
          borderTop: '1px solid rgba(124, 227, 255, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: baseZIndexes.toolbar,
          px: 1.5
        }}
      >
        <Box
          onClick={() => setMenuOpen(prev => !prev)}
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            mr: 1.5,
            cursor: 'pointer',
            position: 'relative',
            display: leftPanelExpanded ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(192, 245, 255, 0.7)',
            background: 'linear-gradient(135deg, rgba(22, 37, 70, 0.9), rgba(22, 25, 54, 0.92))',
            boxShadow: '0 0 14px rgba(112, 221, 255, 0.35), inset 0 0 10px rgba(114, 240, 255, 0.2)',
            overflow: 'hidden',
            '@keyframes skyliteCubeSpin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            },
            '@keyframes skyliteCubePulse': {
              '0%': { opacity: 0.55, filter: 'blur(0px)' },
              '50%': { opacity: 1, filter: 'blur(0.2px)' },
              '100%': { opacity: 0.55, filter: 'blur(0px)' }
            }
          }}
          title="Skylite menu"
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              position: 'relative',
              animation: 'skyliteCubeSpin 9s linear infinite',
              transformOrigin: '50% 50%'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                border: '1px solid rgba(219, 247, 255, 0.95)',
                transform: 'rotate(0deg)',
                boxShadow: '0 0 6px rgba(118, 228, 255, 0.7)'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 2,
                border: '1px solid rgba(166, 239, 255, 0.9)',
                transform: 'rotate(45deg)'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 5,
                border: '1px solid rgba(157, 129, 255, 0.9)',
                transform: 'rotate(25deg)'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 4,
                height: 4,
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'radial-gradient(circle, rgba(244,251,255,1) 0%, rgba(115,229,255,1) 65%, rgba(115,229,255,0) 100%)',
                animation: 'skyliteCubePulse 1.8s ease-in-out infinite'
              }}
            />
          </Box>
        </Box>
        {canShellPreview && !institutionAccessBlocked ? (
          <>
            <Button
              size="small"
              variant="text"
              onClick={(e) => setShellPreviewMenuAnchor(e.currentTarget)}
              title="Temporarily match another role’s Skylite menus and tools (UI only; your session role still applies to APIs)"
              sx={{
                mr: 1,
                minWidth: 0,
                px: 0.75,
                py: 0,
                fontSize: '0.68rem',
                color: 'rgba(220, 245, 255, 0.92)',
                textTransform: 'none',
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(140, 232, 255, 0.12)' }
              }}
            >
              View shell
            </Button>
            <Menu
              anchorEl={shellPreviewMenuAnchor}
              open={Boolean(shellPreviewMenuAnchor)}
              onClose={() => setShellPreviewMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{ sx: { mt: -0.5, minWidth: 220 } }}
            >
              <MenuItem dense onClick={() => applyShellPreview('')}>
                Use session role ({sessionRole})
              </MenuItem>
              <MenuItem dense onClick={() => applyShellPreview('student')}>
                Student shell
              </MenuItem>
              <MenuItem dense onClick={() => applyShellPreview('teacher')}>
                Teacher shell
              </MenuItem>
              <MenuItem dense onClick={() => applyShellPreview('admin')}>
                Admin shell
              </MenuItem>
              <MenuItem dense onClick={() => applyShellPreview('tester')}>
                Tester shell
              </MenuItem>
            </Menu>
          </>
        ) : null}
        {menuOpen && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 34,
              left: 8,
              width: 540,
              minHeight: 280,
              borderRadius: 1.5,
              overflow: 'hidden',
              border: '1px solid rgba(157, 226, 255, 0.35)',
              boxShadow: '0 16px 42px rgba(0,0,0,0.5)',
              background: 'linear-gradient(135deg, rgba(10,16,30,0.98), rgba(17,29,51,0.97))',
              display: 'flex',
              zIndex: baseZIndexes.toolbar + 2
            }}
          >
            <Box sx={{ width: 260, p: 1 }}>
              {visibleToolGroups.map(group => (
                <Box
                  key={group.id}
                  onMouseEnter={() => setActiveMenuGroup(group.id)}
                  sx={{
                    px: 1.2,
                    py: 0.8,
                    borderRadius: 1,
                    cursor: 'pointer',
                    color: activeMenuGroup === group.id ? '#dbf7ff' : '#c3e5ff',
                    backgroundColor: activeMenuGroup === group.id ? 'rgba(91, 206, 255, 0.18)' : 'transparent',
                    fontSize: '0.88rem',
                    letterSpacing: '0.03em',
                    '&:hover': { backgroundColor: 'rgba(91, 206, 255, 0.2)' }
                  }}
                >
                  {group.title}
                </Box>
              ))}
            </Box>
            <Box sx={{ width: 1, backgroundColor: 'rgba(144, 212, 255, 0.22)' }} />
            <Box sx={{ flex: 1, p: 1 }}>
              {(visibleToolGroups.find(group => group.id === activeMenuGroup)?.tools || []).map(tool => (
                <Box
                  key={tool.id}
                  onClick={() => handleMenuToolClick(tool)}
                  sx={{
                    px: 1.2,
                    py: 0.8,
                    borderRadius: 1,
                    cursor: institutionAccessBlocked ? 'not-allowed' : 'pointer',
                    color: institutionAccessBlocked ? 'rgba(237, 248, 255, 0.45)' : '#edf8ff',
                    fontSize: '0.88rem',
                    '&:hover': { backgroundColor: institutionAccessBlocked ? 'transparent' : 'rgba(140, 232, 255, 0.2)' }
                  }}
                >
                  {tool.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {minimizedWindows.map(id => {
          const win = windows.find(w => w.id === id);
          if (!win) return null;
          let IconComponent;
          if (win.type === 'terminal') IconComponent = TerminalIcon;
          else if (win.type === 'report') IconComponent = ScrollIcon;
          else if (win.type === 'files') IconComponent = FolderIcon;
          else if (win.type === 'browser') IconComponent = BugReportIcon;
          else IconComponent = null;
          return (
            <Box
              key={id}
              onClick={() => setMinimizedWindows(prev => prev.filter(minId => minId !== id))}
              sx={{
                mx: 1,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.05)',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.08)'
                }
              }}
            >
              {IconComponent && <IconComponent sx={{ fontSize: 16, color: 'white' }} />}
            </Box>
          );
        })}
        {effectiveIsOwnerConsole && !demoDisplayActive ? (
          <Button
            size="small"
            variant="text"
            startIcon={<DashboardIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              if (ownerInstitutionPreviewActive && supportPreviewUser) {
                handleExitSupportPreview();
                return;
              }
              try {
                sessionStorage.removeItem(OWNER_HUB_SESSION_KEY);
              } catch (_) {
                /* ignore */
              }
              setOwnerHubVisible(true);
              try {
                window.history.replaceState({ ownerHub: true }, '', OWNER_ADMIN_HUB_PATH);
              } catch {
                /* ignore */
              }
            }}
            title={t('ownerTaskbarReturnHubTitle')}
            sx={{
              ml: 'auto',
              minWidth: 0,
              px: 0.75,
              py: 0,
              fontSize: '0.72rem',
              color: 'rgba(220, 245, 255, 0.92)',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(140, 232, 255, 0.12)' }
            }}
          >
            {t('ownerTaskbarAdminDashboard')}
          </Button>
        ) : null}
        {canManageMembershipsUi &&
        !effectiveIsOwnerConsole &&
        institutionDesktopEntered &&
        !institutionAccessBlocked ? (
          <Button
            size="small"
            variant="text"
            startIcon={<AssessmentIcon sx={{ fontSize: 16 }} />}
            onClick={handleReturnToAnalyticsHub}
            title="Return to institution analytics and license hub"
            sx={{
              ml: 'auto',
              minWidth: 0,
              px: 0.75,
              py: 0,
              fontSize: '0.72rem',
              color: 'rgba(220, 245, 255, 0.92)',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(140, 232, 255, 0.12)' }
            }}
          >
            Analytics hub
          </Button>
        ) : null}
        <Button
          size="small"
          variant="text"
          startIcon={<SchoolIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            // Force course list — sticky currentView on :3001 would otherwise reopen the last lesson
            window.location.href = getLearningDashboardUrl({ openDashboard: true });
          }}
          title="Return to Command Line training dashboard"
          sx={{
            ml:
              effectiveIsOwnerConsole
                ? 0
                : canManageMembershipsUi && institutionDesktopEntered
                  ? 0
                  : 'auto',
            minWidth: 0,
            px: 0.75,
            py: 0,
            fontSize: '0.72rem',
            color: 'rgba(220, 245, 255, 0.92)',
            textTransform: 'none',
            '&:hover': { backgroundColor: 'rgba(140, 232, 255, 0.12)' }
          }}
        >
          Learning dashboard
        </Button>
        {effectiveIsOwnerConsole && !demoDisplayActive ? (
          <>
            <Button
              size="small"
              variant="text"
              onClick={() => setLicenseGatePreviewMode('needs_code')}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0,
                fontSize: '0.68rem',
                color: 'rgba(200, 230, 255, 0.75)',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(140, 232, 255, 0.1)' }
              }}
            >
              Preview license
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setLicenseGatePreviewMode('expired')}
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0,
                fontSize: '0.68rem',
                color: 'rgba(200, 230, 255, 0.75)',
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(140, 232, 255, 0.1)' }
              }}
            >
              Preview renewal
            </Button>
          </>
        ) : null}
        <Button
          size="small"
          variant="text"
          startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            if (demoDisplayActive) clearSkyliteDemoBrowserState();
            window.location.href = '/logout';
          }}
          sx={{
            ml: 0,
            minWidth: 0,
            px: 1,
            py: 0,
            fontSize: '0.72rem',
            color: 'rgba(220, 245, 255, 0.92)',
            textTransform: 'none',
            '&:hover': { backgroundColor: 'rgba(140, 232, 255, 0.12)' }
          }}
        >
          Log out
        </Button>
      </Box>
      </Box>
      )}

      {licenseConsolePageOpen && effectiveIsOwnerConsole && !demoDisplayActive ? (
        <Box
          className="notranslate"
          translate="no"
          dir="ltr"
          lang="en"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 26000,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#040810',
            backgroundImage:
              'radial-gradient(ellipse 120% 85% at 50% 0%, rgb(22, 38, 72) 0%, rgb(8, 12, 24) 42%, rgb(3, 5, 12) 100%)',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              pt: 6,
              pb: 1.25,
              borderBottom: '1px solid rgba(120, 200, 255, 0.22)',
              bgcolor: 'rgb(6, 10, 22)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.35)'
            }}
          >
            <Typography sx={{ color: '#e8f4ff', fontWeight: 800, letterSpacing: '0.12em', fontSize: '0.95rem' }}>
              {getTranslation('ownerLicenseConsoleTitle', 'en')}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
              px: { xs: 2, sm: 3 },
              py: 2.5,
              width: '100%',
              maxWidth: 1080,
              mx: 'auto',
              boxSizing: 'border-box',
              bgcolor: '#040810'
            }}
          >
            <OwnerLicenseConsole />
          </Box>
        </Box>
      ) : null}

      <SupportTicketPanel
        open={supportTicketOpen}
        mode={supportTicketMode}
        onClose={() => setSupportTicketOpen(false)}
      />
    </ThemeProvider>
  );
}

export default App; 